import React, { useCallback, useEffect, useState, useRef, Fragment } from 'react';
import { motion } from 'framer-motion';
import { SunIcon, MoonIcon, StarIcon, SparklesIcon, CalendarIcon, ClockIcon, MapPinIcon, HeartIcon, BriefcaseIcon, CompassIcon, ShieldIcon, ZapIcon, TrendingUpIcon, BookOpenIcon, TargetIcon, CheckIcon, ActivityIcon, LayersIcon, UsersIcon, AlertTriangleIcon, MessageCircleIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Card } from '../ui/Card';
import { SOLAR_INTRO, SOLAR_YEAR_THEME, SOLAR_ASCENDANT, SOLAR_SUN_POSITION, SOLAR_MOON, SOLAR_PLANETS_IN_HOUSES, SOLAR_ASPECTS, SOLAR_LIFE_AREAS, SOLAR_RECOMMENDATIONS, SOLAR_SUMMARY } from '../../data/solarReportContent';
import { COMPAT_INTRO, COMPAT_OVERALL_DYNAMICS, COMPAT_SUN_INTERACTION, COMPAT_EMOTIONAL, COMPAT_ROMANTIC, COMPAT_INTELLECTUAL, COMPAT_KARMIC, COMPAT_PLANETS_IN_HOUSES, COMPAT_ASPECTS_DETAILED, COMPAT_STRENGTHS, COMPAT_CONFLICTS, COMPAT_LONGTERM, COMPAT_RECOMMENDATIONS, COMPAT_SUMMARY } from '../../data/compatibilityReportContent';
import { COMBO_NATAL_INTRO, COMBO_PERSONALITY, COMBO_SUN, COMBO_MOON, COMBO_ASCENDANT, COMBO_PLANETS_IN_SIGNS, COMBO_HOUSES_SUMMARY, COMBO_NATAL_ASPECTS, COMBO_NATAL_LIFE_AREAS, COMBO_SOLAR_INTRO, COMBO_YEAR_ENERGY, COMBO_SOLAR_ASCENDANT, COMBO_SOLAR_SUN, COMBO_SOLAR_MOON, COMBO_SOLAR_PLANETS, COMBO_SOLAR_ASPECTS, COMBO_KEY_PERIODS, COMBO_SOLAR_NATAL_INTERACTION, COMBO_YEAR_LIFE_AREAS, COMBO_RECOMMENDATIONS, COMBO_FINAL_SUMMARY } from '../../data/comboReportContent';
import { FORECAST_INTRO, FORECAST_PERIOD_ENERGY, FORECAST_SLOW_TRANSITS, FORECAST_PERSONAL_TRANSITS, FORECAST_HOUSES_ACTIVATION, FORECAST_TRANSIT_ASPECTS, FORECAST_KEY_PERIODS, FORECAST_LIFE_AREAS, FORECAST_OPPORTUNITIES, FORECAST_DIFFICULTIES, FORECAST_RECOMMENDATIONS, FORECAST_SUMMARY, FORECAST_MONTHLY_DETAIL } from '../../data/forecastReportContent';
import { INTRO_CONTENT, EXTENDED_SUN, EXTENDED_MOON, EXTENDED_MERCURY, EXTENDED_VENUS, EXTENDED_MARS, EXTENDED_JUPITER, EXTENDED_SATURN, OUTER_PLANETS_EXTENDED, HOUSES_DETAILED, ASPECTS_DETAILED, HEALTH_SECTION, KARMIC_SECTION, TALENTS_SECTION, CHALLENGES_SECTION, FORECAST_SECTION, FINAL_ANALYSIS, PLANET_POSITIONS_TABLE, ENERGY_RADAR_DATA, ZODIAC_COMPATIBILITY, PERSONAL_AFFIRMATIONS, CURRENT_TRANSITS, CELEBRITY_MATCHES } from '../../data/fullReportContent';
type ServiceType = 'natal' | 'solar' | 'compatibility' | 'combo' | 'forecast';
interface PremiumReportPreviewProps {
  serviceType: ServiceType;
}
// ═══════════════════════════════════════════
// SERVICE-SPECIFIC DATA
// ═══════════════════════════════════════════
const SERVICE_CONFIG: Record<ServiceType, {
  title: string;
  subtitle: string;
  gradient: string;
  accentColor: string;
  badgeClass: string;
  icon: React.ReactNode;
  person2?: {
    name: string;
    date: string;
    time: string;
    place: string;
  };
  extraInfo?: string;
}> = {
  natal: {
    title: 'Натальная карта',
    subtitle: 'Полный астрологический разбор личности',
    gradient: 'from-slate-900 via-indigo-900 to-slate-900',
    accentColor: 'indigo',
    badgeClass: 'bg-indigo-500/30 text-indigo-200 border-indigo-400/30',
    icon: <StarIcon className="w-8 h-8" data-id="element-17712" />
  },
  solar: {
    title: 'Соляр (Прогноз на год)',
    subtitle: 'Персональный астрологический прогноз на 2026 год',
    gradient: 'from-slate-900 via-amber-900 to-slate-900',
    accentColor: 'amber',
    badgeClass: 'bg-amber-500/30 text-amber-200 border-amber-400/30',
    icon: <SunIcon className="w-8 h-8" data-id="element-17713" />,
    extraInfo: 'Солярный год: 2026'
  },
  compatibility: {
    title: 'Совместимость (Синастрия)',
    subtitle: 'Астрологический анализ отношений',
    gradient: 'from-slate-900 via-rose-900 to-slate-900',
    accentColor: 'rose',
    badgeClass: 'bg-rose-500/30 text-rose-200 border-rose-400/30',
    icon: <HeartIcon className="w-8 h-8" data-id="element-17714" />,
    person2: {
      name: 'Мария Иванова',
      date: '23 марта 1994',
      time: '09:15',
      place: 'Санкт-Петербург'
    }
  },
  combo: {
    title: 'Комбо: Натал + Соляр',
    subtitle: 'Полный анализ личности + прогноз на год',
    gradient: 'from-slate-900 via-purple-900 to-slate-900',
    accentColor: 'purple',
    badgeClass: 'bg-purple-500/30 text-purple-200 border-purple-400/30',
    icon: <LayersIcon className="w-8 h-8" data-id="element-17715" />,
    extraInfo: 'Солярный год: 2026'
  },
  forecast: {
    title: 'Прогноз (Транзиты)',
    subtitle: 'Анализ текущих и предстоящих транзитов',
    gradient: 'from-slate-900 via-teal-900 to-slate-900',
    accentColor: 'teal',
    badgeClass: 'bg-teal-500/30 text-teal-200 border-teal-400/30',
    icon: <ActivityIcon className="w-8 h-8" data-id="element-17716" />,
    extraInfo: 'Период: Март 2026 — Сентябрь 2026'
  }
};
const REPORT_PAGE_COUNT: Record<ServiceType, number> = {
  natal: 35,
  solar: 38,
  compatibility: 32,
  combo: 62,
  forecast: 30
};
const TOC_BY_SERVICE: Record<ServiceType, {
  num: string;
  title: string;
}[]> = {
  natal: [{
    num: '01',
    title: 'Введение'
  }, {
    num: '02',
    title: 'Общий психологический портрет'
  }, {
    num: '03',
    title: 'Солнце — Ядро личности'
  }, {
    num: '04',
    title: 'Луна — Внутренний мир'
  }, {
    num: '05',
    title: 'Асцендент — Социальная маска'
  }, {
    num: '06',
    title: 'Меркурий — Мышление'
  }, {
    num: '07',
    title: 'Венера — Любовь и ценности'
  }, {
    num: '08',
    title: 'Марс — Энергия и действие'
  }, {
    num: '09',
    title: 'Планеты в домах'
  }, {
    num: '10',
    title: 'Аспекты между планетами'
  }, {
    num: '11',
    title: 'Карьера и финансы'
  }, {
    num: '12',
    title: 'Любовь и отношения'
  }, {
    num: '13',
    title: 'Кармические задачи'
  }, {
    num: '14',
    title: 'Таланты и предназначение'
  }, {
    num: '15',
    title: 'Итоговый анализ'
  }],
  solar: [{
    num: '01',
    title: 'Введение'
  }, {
    num: '02',
    title: 'Общая тема года'
  }, {
    num: '03',
    title: 'Асцендент соляра'
  }, {
    num: '04',
    title: 'Положение Солнца в соляре'
  }, {
    num: '05',
    title: 'Луна в соляре'
  }, {
    num: '06',
    title: 'Меркурий в соляре'
  }, {
    num: '07',
    title: 'Венера в соляре'
  }, {
    num: '08',
    title: 'Марс в соляре'
  }, {
    num: '09',
    title: 'Юпитер, Сатурн и внешние планеты'
  }, {
    num: '10',
    title: 'Аспекты соляра'
  }, {
    num: '11',
    title: 'Карьера и финансы'
  }, {
    num: '12',
    title: 'Отношения и семья'
  }, {
    num: '13',
    title: 'Личностное развитие и здоровье'
  }, {
    num: '14',
    title: 'Важные периоды года'
  }, {
    num: '15',
    title: 'Рекомендации на год'
  }, {
    num: '16',
    title: 'Итог прогноза'
  }],
  compatibility: [{
    num: '01',
    title: 'Введение'
  }, {
    num: '02',
    title: 'Общая динамика отношений'
  }, {
    num: '03',
    title: 'Солнце и личность партнёров'
  }, {
    num: '04',
    title: 'Эмоциональная совместимость'
  }, {
    num: '05',
    title: 'Романтическая и сексуальная совместимость'
  }, {
    num: '06',
    title: 'Интеллектуальная совместимость'
  }, {
    num: '07',
    title: 'Кармические связи'
  }, {
    num: '08',
    title: 'Планеты партнёра в домах другого'
  }, {
    num: '09',
    title: 'Основные аспекты между картами'
  }, {
    num: '10',
    title: 'Сильные стороны отношений'
  }, {
    num: '11',
    title: 'Возможные конфликты'
  }, {
    num: '12',
    title: 'Потенциал долгосрочных отношений'
  }, {
    num: '13',
    title: 'Практические рекомендации'
  }, {
    num: '14',
    title: 'Итог анализа'
  }],
  combo: [{
    num: '01',
    title: 'Введение'
  }, {
    num: '02',
    title: 'Общая характеристика личности'
  }, {
    num: '03',
    title: 'Солнце — Ядро личности'
  }, {
    num: '04',
    title: 'Луна — Эмоциональная природа'
  }, {
    num: '05',
    title: 'Асцендент — Внешнее проявление'
  }, {
    num: '06',
    title: 'Планеты в знаках'
  }, {
    num: '07',
    title: 'Планеты в домах'
  }, {
    num: '08',
    title: 'Аспекты натальной карты'
  }, {
    num: '09',
    title: 'Сферы жизни (натал)'
  }, {
    num: '10',
    title: 'Введение в соляр'
  }, {
    num: '11',
    title: 'Общая энергия 2026 года'
  }, {
    num: '12',
    title: 'Асцендент соляра'
  }, {
    num: '13',
    title: 'Солнце в соляре'
  }, {
    num: '14',
    title: 'Луна в соляре'
  }, {
    num: '15',
    title: 'Планеты соляра в домах'
  }, {
    num: '16',
    title: 'Аспекты соляра'
  }, {
    num: '17',
    title: 'Важные периоды года'
  }, {
    num: '18',
    title: 'Влияние соляра на натал'
  }, {
    num: '19',
    title: 'Сферы жизни (год)'
  }, {
    num: '20',
    title: 'Рекомендации на год'
  }, {
    num: '21',
    title: 'Итоговый анализ'
  }],
  forecast: [{
    num: '01',
    title: 'Введение'
  }, {
    num: '02',
    title: 'Общая энергия периода'
  }, {
    num: '03',
    title: 'Медленные транзиты'
  }, {
    num: '04',
    title: 'Транзиты личных планет'
  }, {
    num: '05',
    title: 'Транзиты по домам'
  }, {
    num: '06',
    title: 'Аспекты транзитов'
  }, {
    num: '07',
    title: 'Ключевые периоды'
  }, {
    num: '08',
    title: 'Сферы жизни'
  }, {
    num: '09',
    title: 'Возможности периода'
  }, {
    num: '10',
    title: 'Потенциальные трудности'
  }, {
    num: '11',
    title: 'Рекомендации'
  }, {
    num: '12',
    title: 'Итог прогноза'
  }]
};
// ═══════════════════════════════════════════
// NATAL DATA
// ═══════════════════════════════════════════
const NATAL_ELEMENT_BALANCE = [{
  element: 'Огонь',
  percent: 20,
  color: 'bg-red-500',
  planets: 'Солнце, Меркурий'
}, {
  element: 'Земля',
  percent: 30,
  color: 'bg-emerald-500',
  planets: 'Юпитер, Уран, Нептун'
}, {
  element: 'Воздух',
  percent: 20,
  color: 'bg-blue-500',
  planets: 'Марс, Сатурн'
}, {
  element: 'Вода',
  percent: 30,
  color: 'bg-indigo-500',
  planets: 'Луна, Венера, Плутон'
}];
const NATAL_ASPECTS_GRID = [{
  planets: '☉ ☌ ☿',
  type: 'Соединение',
  nature: 'intense'
}, {
  planets: '☽ △ ♀',
  type: 'Трин',
  nature: 'harmonious'
}, {
  planets: '♀ ⚹ ♃',
  type: 'Секстиль',
  nature: 'harmonious'
}, {
  planets: '♂ □ ♃',
  type: 'Квадрат',
  nature: 'tense'
}, {
  planets: '♂ □ ♄',
  type: 'Квадрат',
  nature: 'tense'
}];
// ═══════════════════════════════════════════
// SOLAR DATA
// ═══════════════════════════════════════════
const SOLAR_MONTHLY_FORECAST = [{
  month: 'Янв',
  intensity: 60,
  theme: 'Планирование'
}, {
  month: 'Фев',
  intensity: 55,
  theme: 'Подготовка'
}, {
  month: 'Мар',
  intensity: 90,
  theme: 'Прорыв'
}, {
  month: 'Апр',
  intensity: 85,
  theme: 'Рост'
}, {
  month: 'Май',
  intensity: 80,
  theme: 'Реализация'
}, {
  month: 'Июн',
  intensity: 65,
  theme: 'Стабилизация'
}, {
  month: 'Июл',
  intensity: 50,
  theme: 'Отдых'
}, {
  month: 'Авг',
  intensity: 70,
  theme: 'Новый цикл'
}, {
  month: 'Сен',
  intensity: 75,
  theme: 'Активность'
}, {
  month: 'Окт',
  intensity: 60,
  theme: 'Завершение'
}, {
  month: 'Ноя',
  intensity: 55,
  theme: 'Рефлексия'
}, {
  month: 'Дек',
  intensity: 65,
  theme: 'Итоги'
}];
const SOLAR_KEY_DATES = [{
  date: '15 марта',
  event: 'Новолуние в 10 доме',
  type: 'positive'
}, {
  date: '28 апреля',
  event: 'Юпитер тригон Солнце',
  type: 'positive'
}, {
  date: '12 июня',
  event: 'Сатурн квадрат Луна',
  type: 'caution'
}, {
  date: '8 августа',
  event: 'День рождения — новый соляр',
  type: 'neutral'
}, {
  date: '22 октября',
  event: 'Затмение в 5 доме',
  type: 'caution'
}, {
  date: '5 декабря',
  event: 'Венера секстиль Юпитер',
  type: 'positive'
}];
// ═══════════════════════════════════════════
// COMPATIBILITY DATA
// ═══════════════════════════════════════════
const COMPATIBILITY_SCORES = [{
  area: 'Эмоциональная связь',
  percent: 85,
  color: 'bg-rose-500'
}, {
  area: 'Романтика и страсть',
  percent: 92,
  color: 'bg-pink-500'
}, {
  area: 'Коммуникация',
  percent: 78,
  color: 'bg-sky-500'
}, {
  area: 'Быт и финансы',
  percent: 70,
  color: 'bg-emerald-500'
}, {
  area: 'Долгосрочные перспективы',
  percent: 88,
  color: 'bg-purple-500'
}];
const COMPATIBILITY_ASPECTS = [{
  aspect: '☉ △ ☽',
  desc: 'Солнце — Луна',
  nature: 'harmonious'
}, {
  aspect: '♀ ☌ ♂',
  desc: 'Венера — Марс',
  nature: 'intense'
}, {
  aspect: '☽ ⚹ ☽',
  desc: 'Луна — Луна',
  nature: 'harmonious'
}, {
  aspect: '☿ □ ☿',
  desc: 'Меркурий — Меркурий',
  nature: 'tense'
}, {
  aspect: '♃ △ ♀',
  desc: 'Юпитер — Венера',
  nature: 'harmonious'
}];
// ═══════════════════════════════════════════
// FORECAST DATA
// ═══════════════════════════════════════════
const FORECAST_ACTIVE_TRANSITS = [{
  planet: 'Юпитер',
  position: 'в 10 доме',
  effect: 'Карьерный рост',
  strength: 90,
  color: 'bg-amber-500'
}, {
  planet: 'Сатурн',
  position: 'в 7 доме',
  effect: 'Уроки в отношениях',
  strength: 75,
  color: 'bg-slate-500'
}, {
  planet: 'Уран',
  position: 'в 1 доме',
  effect: 'Личная трансформация',
  strength: 85,
  color: 'bg-cyan-500'
}, {
  planet: 'Нептун',
  position: 'в 9 доме',
  effect: 'Духовный поиск',
  strength: 60,
  color: 'bg-indigo-500'
}, {
  planet: 'Плутон',
  position: 'в 8 доме',
  effect: 'Глубокие перемены',
  strength: 70,
  color: 'bg-purple-500'
}];
const FORECAST_TIMELINE = [{
  period: 'Март',
  events: ['Юпитер тригон Солнце', 'Новолуние в 10 доме'],
  intensity: 'high'
}, {
  period: 'Апрель',
  events: ['Меркурий ретро', 'Венера в 7 доме'],
  intensity: 'medium'
}, {
  period: 'Май',
  events: ['Затмение в 11 доме', 'Марс секстиль Юпитер'],
  intensity: 'high'
}, {
  period: 'Июнь',
  events: ['Сатурн квадрат Луна'],
  intensity: 'low'
}];
export function PremiumReportPreview({
  serviceType
}: PremiumReportPreviewProps) {
  const config = SERVICE_CONFIG[serviceType];
  const toc = TOC_BY_SERVICE[serviceType];
  // Review state
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [reviewTags, setReviewTags] = useState<string[]>([]);
  const [reviewText, setReviewText] = useState('');
  const handlePrint = useCallback(() => {
    window.print();
  }, []);
  // ═══════════════════════════════════════════
  // RENDER NATAL CONTENT
  // ═══════════════════════════════════════════
  const renderNatalContent = () => {
    const SectionHeader = ({
      icon,
      title,
      subtitle,
      color = 'indigo'
    }: {
      icon: React.ReactNode;
      title: string;
      subtitle?: string;
      color?: string;
    }) => <div className="flex items-center gap-3 mb-4 sm:mb-6" data-id="element-17717">
        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-${color}-100 text-${color}-600 flex items-center justify-center`} data-id="element-17718">
          {icon}
        </div>
        <div className="min-w-0" data-id="element-17719">
          <h2 className="text-lg sm:text-xl font-bold text-slate-900 leading-tight" data-id="element-17720">
            {title}
          </h2>
          {subtitle && <p className="text-xs sm:text-sm text-slate-500" data-id="element-17721">{subtitle}</p>}
        </div>
      </div>;
    const renderParagraphs = (text: string) => {
      return text.split('\n\n').map((paragraph, i) => <p key={i} className="text-sm sm:text-base text-slate-600 leading-relaxed mb-3 sm:mb-4 last:mb-0" data-id="element-17722">
          {paragraph}
        </p>);
    };
    return <>
        {/* 01. ВВЕДЕНИЕ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17723">
          <SectionHeader icon={<BookOpenIcon className="w-5 h-5" data-id="element-17725" />} title={INTRO_CONTENT.title} subtitle="Что такое натальная карта и как работает астрология" data-id="element-17724" />
          <div className="space-y-6" data-id="element-17726">
            <div data-id="element-17727">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17728">
                Что такое натальная карта?
              </h3>
              {renderParagraphs(INTRO_CONTENT.whatIsNatalChart)}
            </div>
            <div data-id="element-17729">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17730">
                Как это работает?
              </h3>
              {renderParagraphs(INTRO_CONTENT.howItWorks)}
            </div>
            <div className="bg-indigo-50/50 p-5 rounded-xl border border-indigo-100" data-id="element-17731">
              <h3 className="font-bold text-indigo-800 mb-2" data-id="element-17732">
                Как читать этот отчёт
              </h3>
              {renderParagraphs(INTRO_CONTENT.howToRead)}
            </div>
          </div>
        </div>

        {/* 02. ОБЩИЙ ПСИХОЛОГИЧЕСКИЙ ПОРТРЕТ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-indigo-50/30 to-white" data-id="element-17733">
          <SectionHeader icon={<SparklesIcon className="w-5 h-5" data-id="element-17735" />} title="Общий психологический портрет" subtitle="Фундамент вашей личности" data-id="element-17734" />

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-8" data-id="element-17736">
            <Card padding="md" className="bg-white border-amber-200 shadow-sm" data-id="element-17737">
              <div className="flex items-center gap-2 mb-3" data-id="element-17738">
                <SunIcon className="w-5 h-5 text-amber-500" data-id="element-17739" />
                <span className="text-xs font-bold text-amber-600 uppercase tracking-wider" data-id="element-17740">
                  Солнце (Эго)
                </span>
              </div>
              <div className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-id="element-17741">
                <span className="text-amber-500" data-id="element-17742">♌</span> Лев
              </div>
              <p className="text-xs text-slate-500 mt-2" data-id="element-17743">
                Обитель Солнца • Огненная стихия
              </p>
            </Card>

            <Card padding="md" className="bg-white border-indigo-200 shadow-sm" data-id="element-17744">
              <div className="flex items-center gap-2 mb-3" data-id="element-17745">
                <MoonIcon className="w-5 h-5 text-indigo-500" data-id="element-17746" />
                <span className="text-xs font-bold text-indigo-600 uppercase tracking-wider" data-id="element-17747">
                  Луна (Душа)
                </span>
              </div>
              <div className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-id="element-17748">
                <span className="text-indigo-500" data-id="element-17749">♏</span> Скорпион
              </div>
              <p className="text-xs text-slate-500 mt-2" data-id="element-17750">
                Падение Луны • Водная стихия
              </p>
            </Card>

            <Card padding="md" className="bg-white border-purple-200 shadow-sm" data-id="element-17751">
              <div className="flex items-center gap-2 mb-3" data-id="element-17752">
                <StarIcon className="w-5 h-5 text-purple-500" data-id="element-17753" />
                <span className="text-xs font-bold text-purple-600 uppercase tracking-wider" data-id="element-17754">
                  Асцендент (Маска)
                </span>
              </div>
              <div className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-id="element-17755">
                <span className="text-purple-500" data-id="element-17756">♐</span> Стрелец
              </div>
              <p className="text-xs text-slate-500 mt-2" data-id="element-17757">
                Управитель: Юпитер • Огненная стихия
              </p>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8" data-id="element-17758">
            <div data-id="element-17759">
              <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4" data-id="element-17760">
                Баланс стихий
              </h3>
              <div className="space-y-4" data-id="element-17761">
                {NATAL_ELEMENT_BALANCE.map((item, i) => <div key={i} data-id="element-17762">
                    <div className="flex justify-between text-sm mb-1.5" data-id="element-17763">
                      <span className="font-medium text-slate-700" data-id="element-17764">
                        {item.element} ({item.percent}%)
                      </span>
                      <span className="text-slate-400 text-xs" data-id="element-17765">
                        {item.planets}
                      </span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden" data-id="element-17766">
                      <motion.div className={`${item.color} h-2.5 rounded-full`} initial={{
                    width: 0
                  }} animate={{
                    width: `${item.percent}%`
                  }} transition={{
                    duration: 1,
                    delay: i * 0.15,
                    ease: 'easeOut'
                  }} data-id="element-17767" />
                    </div>
                  </div>)}
              </div>
            </div>

            <div data-id="element-17768">
              <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4" data-id="element-17769">
                Энергетический профиль
              </h3>
              <div className="space-y-4" data-id="element-17770">
                {ENERGY_RADAR_DATA.axes.map((item, i) => <div key={i} data-id="element-17771">
                    <div className="flex justify-between text-sm mb-1.5" data-id="element-17772">
                      <span className="font-medium text-slate-700" data-id="element-17773">
                        {item.label}
                      </span>
                      <span className="font-bold text-indigo-600" data-id="element-17774">
                        {item.value}/100
                      </span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden" data-id="element-17775">
                      <motion.div className="bg-indigo-500 h-2.5 rounded-full" initial={{
                    width: 0
                  }} animate={{
                    width: `${item.value}%`
                  }} transition={{
                    duration: 1,
                    delay: i * 0.1,
                    ease: 'easeOut'
                  }} data-id="element-17776" />
                    </div>
                  </div>)}
              </div>
            </div>
          </div>

          <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4" data-id="element-17777">
            Положения планет
          </h3>
          <div className="-mx-4 sm:mx-0 overflow-x-auto" data-id="element-17778">
            <table className="w-full text-sm text-left border-collapse min-w-[500px]" data-id="element-17779">
              <thead className="bg-slate-50 text-slate-500 font-medium" data-id="element-17780">
                <tr data-id="element-17781">
                  <th className="px-4 py-3 rounded-tl-lg" data-id="element-17782">Планета</th>
                  <th className="px-4 py-3" data-id="element-17783">Знак</th>
                  <th className="px-4 py-3" data-id="element-17784">Градус</th>
                  <th className="px-4 py-3" data-id="element-17785">Дом</th>
                  <th className="px-4 py-3 rounded-tr-lg" data-id="element-17786">Достоинство</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white" data-id="element-17787">
                {PLANET_POSITIONS_TABLE.map((planet, i) => <tr key={i} className="hover:bg-slate-50/50 transition-colors" data-id="element-17788">
                    <td className="px-4 py-3 font-medium text-slate-900 flex items-center gap-2" data-id="element-17789">
                      <span className="text-lg text-indigo-500 w-6 text-center" data-id="element-17790">
                        {planet.symbol}
                      </span>
                      {planet.planet}
                    </td>
                    <td className="px-4 py-3 text-slate-700" data-id="element-17791">{planet.sign}</td>
                    <td className="px-4 py-3 text-slate-500 font-mono text-xs" data-id="element-17792">
                      {planet.degree}
                    </td>
                    <td className="px-4 py-3 text-slate-700" data-id="element-17793">{planet.house}</td>
                    <td className="px-4 py-3 text-slate-500" data-id="element-17794">
                      {planet.dignity}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>

        {/* 03. СОЛНЦЕ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17795">
          <SectionHeader icon={<SunIcon className="w-5 h-5" data-id="element-17797" />} title="Солнце во Льве" subtitle="Ядро личности, эго и жизненная сила" color="amber" data-id="element-17796" />
          <div className="space-y-6" data-id="element-17798">
            <div data-id="element-17799">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17800">Жизненная цель</h3>
              {renderParagraphs(EXTENDED_SUN.lifePurpose)}
            </div>
            <div data-id="element-17801">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17802">
                В повседневной жизни
              </h3>
              {renderParagraphs(EXTENDED_SUN.dailyLife)}
            </div>
            <div data-id="element-17803">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17804">В отношениях</h3>
              {renderParagraphs(EXTENDED_SUN.inRelationships)}
            </div>
          </div>
        </div>

        {/* 04. ЛУНА */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-indigo-50/30 to-white" data-id="element-17805">
          <SectionHeader icon={<MoonIcon className="w-5 h-5" data-id="element-17807" />} title="Луна в Скорпионе" subtitle="Внутренний мир, эмоции и подсознание" color="indigo" data-id="element-17806" />
          <div className="space-y-6" data-id="element-17808">
            <div data-id="element-17809">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17810">
                Эмоциональные паттерны
              </h3>
              {renderParagraphs(EXTENDED_MOON.emotionalPatterns)}
            </div>
            <div data-id="element-17811">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17812">
                Отношения с матерью
              </h3>
              {renderParagraphs(EXTENDED_MOON.motherRelation)}
            </div>
            <div className="bg-indigo-50/50 p-5 rounded-xl border border-indigo-100" data-id="element-17813">
              <h3 className="font-bold text-indigo-800 mb-2" data-id="element-17814">
                Скрытые потребности
              </h3>
              {renderParagraphs(EXTENDED_MOON.hiddenNeeds)}
            </div>
          </div>
        </div>

        {/* 05. АСЦЕНДЕНТ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17815">
          <SectionHeader icon={<StarIcon className="w-5 h-5" data-id="element-17817" />} title="Асцендент в Стрельце" subtitle="Социальная маска и первое впечатление" color="purple" data-id="element-17816" />
          <div className="space-y-4" data-id="element-17818">
            <p className="text-slate-600 leading-relaxed" data-id="element-17819">
              Асцендент в Стрельце формирует ваш внешний образ — оптимистичный,
              открытый и любознательный. Люди воспринимают вас как человека с
              широким кругозором и философским взглядом на жизнь. Вы производите
              впечатление человека, который знает, куда идёт, и приглашает
              других присоединиться к путешествию.
            </p>
            <p className="text-slate-600 leading-relaxed" data-id="element-17820">
              Ваше тело, скорее всего, крупное или высокое, с широкими жестами и
              открытой позой. Вы излучаете энтузиазм и уверенность. Однако за
              этой жизнерадостной маской скрывается глубокий и интенсивный
              внутренний мир (Луна в Скорпионе), который вы открываете далеко не
              всем.
            </p>
          </div>
        </div>

        {/* 06. МЕРКУРИЙ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-sky-50/30 to-white" data-id="element-17821">
          <SectionHeader icon={<MessageCircleIcon className="w-5 h-5" data-id="element-17823" />} title="Меркурий во Льве" subtitle="Мышление, речь и коммуникация" color="sky" data-id="element-17822" />
          <div className="space-y-6" data-id="element-17824">
            <div data-id="element-17825">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17826">Стиль обучения</h3>
              {renderParagraphs(EXTENDED_MERCURY.learningStyle)}
            </div>
            <div data-id="element-17827">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17828">Стиль общения</h3>
              {renderParagraphs(EXTENDED_MERCURY.communicationStyle)}
            </div>
            <div data-id="element-17829">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17830">
                Принятие решений
              </h3>
              {renderParagraphs(EXTENDED_MERCURY.decisionMaking)}
            </div>
          </div>
        </div>

        {/* 07. ВЕНЕРА */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17831">
          <SectionHeader icon={<HeartIcon className="w-5 h-5" data-id="element-17833" />} title="Венера в Раке" subtitle="Любовь, ценности и эстетика" color="rose" data-id="element-17832" />
          <div className="space-y-6" data-id="element-17834">
            <div data-id="element-17835">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17836">Язык любви</h3>
              {renderParagraphs(EXTENDED_VENUS.loveLanguage)}
            </div>
            <div data-id="element-17837">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17838">Эстетика и вкус</h3>
              {renderParagraphs(EXTENDED_VENUS.aesthetics)}
            </div>
            <div data-id="element-17839">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17840">
                Финансовые привычки
              </h3>
              {renderParagraphs(EXTENDED_VENUS.financialHabits)}
            </div>
          </div>
        </div>

        {/* 08. МАРС */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-amber-50/30 to-white" data-id="element-17841">
          <SectionHeader icon={<ZapIcon className="w-5 h-5" data-id="element-17843" />} title="Марс в Близнецах" subtitle="Энергия, действие и конфликт" color="amber" data-id="element-17842" />
          <div className="space-y-6" data-id="element-17844">
            <div data-id="element-17845">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17846">Стиль конфликта</h3>
              {renderParagraphs(EXTENDED_MARS.conflictStyle)}
            </div>
            <div data-id="element-17847">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17848">
                Амбиции и действия
              </h3>
              {renderParagraphs(EXTENDED_MARS.ambitionStyle)}
            </div>
            <div data-id="element-17849">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17850">
                Физическая энергия
              </h3>
              {renderParagraphs(EXTENDED_MARS.physicalEnergy)}
            </div>
          </div>
        </div>

        {/* 09. ПЛАНЕТЫ В ДОМАХ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17851">
          <SectionHeader icon={<LayersIcon className="w-5 h-5" data-id="element-17853" />} title="Планеты в домах" subtitle="Сферы жизни и где проявляется ваша энергия" color="indigo" data-id="element-17852" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-17854">
            {HOUSES_DETAILED.map((house, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.05
          }} className={`rounded-xl p-5 border ${house.planets.length > 0 ? 'bg-indigo-50/50 border-indigo-200 shadow-sm' : 'bg-slate-50 border-slate-200'}`} data-id="element-17855">
                <div className="flex items-center justify-between mb-3" data-id="element-17856">
                  <div className="flex items-center gap-2" data-id="element-17857">
                    <span className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold ${house.planets.length > 0 ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-200 text-slate-600'}`} data-id="element-17858">
                      {house.num}
                    </span>
                    <h4 className="font-bold text-slate-900 text-sm" data-id="element-17859">
                      {house.theme}
                    </h4>
                  </div>
                  <span className="text-xs font-medium text-slate-500" data-id="element-17860">
                    {house.sign}
                  </span>
                </div>
                {house.planets.length > 0 && <div className="mb-2 flex flex-wrap gap-1" data-id="element-17861">
                    {house.planets.map((p, idx) => <Badge key={idx} variant="pro" className="text-[10px] px-1.5 py-0" data-id="element-17862">
                        {p}
                      </Badge>)}
                  </div>}
                <p className="text-sm text-slate-600 leading-relaxed" data-id="element-17863">
                  {house.text}
                </p>
              </motion.div>)}
          </div>
        </div>

        {/* 10. АСПЕКТЫ МЕЖДУ ПЛАНЕТАМИ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-indigo-50/30 to-white" data-id="element-17864">
          <SectionHeader icon={<ActivityIcon className="w-5 h-5" data-id="element-17866" />} title="Аспекты между планетами" subtitle="Внутренние диалоги и конфликты" color="indigo" data-id="element-17865" />
          <div className="space-y-4" data-id="element-17867">
            {ASPECTS_DETAILED.map((aspect, i) => {
            const isHarmonious = aspect.nature === 'harmonious';
            const isIntense = aspect.nature === 'intense';
            return <motion.div key={i} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: i * 0.08
            }} className={`rounded-xl p-5 border ${isHarmonious ? 'bg-emerald-50/50 border-emerald-200' : isIntense ? 'bg-purple-50/50 border-purple-200' : 'bg-rose-50/50 border-rose-200'}`} data-id="element-17868">
                  <div className="flex flex-wrap items-start sm:items-center justify-between gap-2 sm:gap-3 mb-3" data-id="element-17869">
                    <div className="flex items-center gap-2 sm:gap-3" data-id="element-17870">
                      <span className={`text-lg font-bold ${isHarmonious ? 'text-emerald-600' : isIntense ? 'text-purple-600' : 'text-rose-600'}`} data-id="element-17871">
                        {aspect.symbol}
                      </span>
                      <h4 className="font-bold text-slate-900 text-sm sm:text-base" data-id="element-17872">
                        {aspect.title}
                      </h4>
                    </div>
                    <Badge variant={isHarmonious ? 'success' : isIntense ? 'pro' : 'warning'} className="text-xs" data-id="element-17873">
                      {isHarmonious ? 'Гармоничный' : isIntense ? 'Интенсивный' : 'Напряжённый'}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed" data-id="element-17874">
                    {aspect.text}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>

        {/* 11. КАРЬЕРА И ФИНАНСЫ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17875">
          <SectionHeader icon={<BriefcaseIcon className="w-5 h-5" data-id="element-17877" />} title="Карьера и финансы" subtitle="Профессиональный потенциал и заработок" color="emerald" data-id="element-17876" />
          <div className="space-y-6" data-id="element-17878">
            <div data-id="element-17879">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17880">
                Профессиональный путь
              </h3>
              {renderParagraphs(EXTENDED_JUPITER)}
            </div>
            <div data-id="element-17881">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17882">
                Финансовая стратегия
              </h3>
              {renderParagraphs(OUTER_PLANETS_EXTENDED.uranus)}
            </div>
            <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-200" data-id="element-17883">
              <h3 className="font-bold text-emerald-800 mb-3" data-id="element-17884">
                Лучшие сферы для реализации
              </h3>
              <ul className="space-y-2" data-id="element-17885">
                {TALENTS_SECTION.bestAreas.split('\n').filter(line => line.trim().startsWith('•')).map((area, i) => <li key={i} className="flex items-start gap-2 text-sm text-emerald-700" data-id="element-17886">
                      <CheckIcon className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-17887" />
                      {area.replace('•', '').trim()}
                    </li>)}
              </ul>
            </div>
          </div>
        </div>

        {/* 12. ЛЮБОВЬ И ОТНОШЕНИЯ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-rose-50/30 to-white" data-id="element-17888">
          <SectionHeader icon={<HeartIcon className="w-5 h-5" data-id="element-17890" />} title="Любовь и отношения" subtitle="Совместимость и партнёрство" color="rose" data-id="element-17889" />
          <div className="space-y-6 mb-8" data-id="element-17891">
            <p className="text-slate-600 leading-relaxed" data-id="element-17892">
              Ваш подход к отношениям формируется Венерой в Раке (потребность в
              заботе и уюте), Марсом в Близнецах (потребность в интеллектуальном
              стимулировании) и 7 домом в Близнецах. Вы ищете партнёра, с
              которым можно и глубоко помолчать, и жарко поспорить.
            </p>
          </div>

          <h3 className="font-bold text-slate-800 mb-4" data-id="element-17893">
            Совместимость со знаками зодиака
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-id="element-17894">
            {ZODIAC_COMPATIBILITY.map((sign, i) => <div key={i} className={`p-3 rounded-xl border ${sign.best ? 'bg-rose-50 border-rose-200 shadow-sm' : 'bg-white border-slate-200'}`} data-id="element-17895">
                <div className="flex items-center justify-between mb-2" data-id="element-17896">
                  <div className="flex items-center gap-1.5" data-id="element-17897">
                    <span className={`text-lg ${sign.best ? 'text-rose-500' : 'text-slate-400'}`} data-id="element-17898">
                      {sign.symbol}
                    </span>
                    <span className="font-bold text-slate-800 text-sm" data-id="element-17899">
                      {sign.sign}
                    </span>
                  </div>
                  <span className={`text-xs font-bold ${sign.percent >= 80 ? 'text-emerald-600' : sign.percent >= 70 ? 'text-amber-600' : 'text-slate-500'}`} data-id="element-17900">
                    {sign.percent}%
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-1.5 mb-2 overflow-hidden" data-id="element-17901">
                  <div className={`h-1.5 rounded-full ${sign.percent >= 80 ? 'bg-emerald-500' : sign.percent >= 70 ? 'bg-amber-500' : 'bg-slate-400'}`} style={{
                width: `${sign.percent}%`
              }} data-id="element-17902" />
                </div>
                <p className="text-[10px] text-slate-500 leading-tight" data-id="element-17903">
                  {sign.note}
                </p>
              </div>)}
          </div>
        </div>

        {/* 13. КАРМИЧЕСКИЕ ЗАДАЧИ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17904">
          <SectionHeader icon={<CompassIcon className="w-5 h-5" data-id="element-17906" />} title="Кармические задачи" subtitle="Предназначение и уроки души" color="purple" data-id="element-17905" />
          <div className="space-y-6" data-id="element-17907">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-17908">
              <div className="bg-purple-50 rounded-xl p-5 border border-purple-200" data-id="element-17909">
                <h3 className="font-bold text-purple-800 mb-2 flex items-center gap-2" data-id="element-17910">
                  <TrendingUpIcon className="w-4 h-4" data-id="element-17911" /> Вектор развития
                  (Северный узел)
                </h3>
                {renderParagraphs(KARMIC_SECTION.northNode)}
              </div>
              <div className="bg-slate-50 rounded-xl p-5 border border-slate-200" data-id="element-17912">
                <h3 className="font-bold text-slate-700 mb-2 flex items-center gap-2" data-id="element-17913">
                  <AlertTriangleIcon className="w-4 h-4" data-id="element-17914" /> Прошлый опыт (Южный
                  узел)
                </h3>
                {renderParagraphs(KARMIC_SECTION.southNode)}
              </div>
            </div>

            <div data-id="element-17915">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17916">
                Главный урок Сатурна
              </h3>
              {renderParagraphs(KARMIC_SECTION.saturnLesson)}
            </div>

            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white shadow-lg" data-id="element-17917">
              <h3 className="font-bold text-white mb-2 text-lg" data-id="element-17918">
                Жизненная миссия
              </h3>
              <p className="text-indigo-100 leading-relaxed" data-id="element-17919">
                {KARMIC_SECTION.lifeMission}
              </p>
            </div>

            <div className="mt-8" data-id="element-17920">
              <h3 className="font-bold text-slate-800 mb-4" data-id="element-17921">Главные вызовы</h3>
              <div className="space-y-3" data-id="element-17922">
                {CHALLENGES_SECTION.mainChallenges.split('\n\n').filter(p => !p.startsWith('Каждая')).map((challenge, i) => <div key={i} className="flex items-start gap-3 bg-white p-4 rounded-xl border border-rose-100" data-id="element-17923">
                      <div className="w-6 h-6 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5" data-id="element-17924">
                        {i + 1}
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed" data-id="element-17925">
                        {challenge}
                      </p>
                    </div>)}
              </div>
            </div>
          </div>
        </div>

        {/* 14. ТАЛАНТЫ И ПРЕДНАЗНАЧЕНИЕ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-amber-50/30 to-white" data-id="element-17926">
          <SectionHeader icon={<TargetIcon className="w-5 h-5" data-id="element-17928" />} title="Таланты и здоровье" subtitle="Ваши сильные стороны и физический потенциал" color="amber" data-id="element-17927" />

          <div className="space-y-8" data-id="element-17929">
            <div data-id="element-17930">
              <h3 className="font-bold text-slate-800 mb-4" data-id="element-17931">
                Природные способности
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-17932">
                {TALENTS_SECTION.naturalAbilities.split('\n\n').filter(p => !p.startsWith('На основе')).map((talent, i) => <div key={i} className="bg-white rounded-xl p-4 border border-amber-200 shadow-sm" data-id="element-17933">
                      <p className="text-sm text-slate-700 leading-relaxed" data-id="element-17934">
                        {talent}
                      </p>
                    </div>)}
              </div>
            </div>

            <div data-id="element-17935">
              <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2" data-id="element-17936">
                <ActivityIcon className="w-5 h-5 text-emerald-500" data-id="element-17937" />{' '}
                Астрологическое здоровье
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4" data-id="element-17938">
                {HEALTH_SECTION.overview}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-17939">
                <Card padding="md" className="bg-emerald-50/50 border-emerald-100" data-id="element-17940">
                  <h4 className="font-bold text-emerald-800 mb-2 text-sm" data-id="element-17941">
                    Витальность (Солнце)
                  </h4>
                  <p className="text-xs text-slate-600" data-id="element-17942">
                    {HEALTH_SECTION.sunHealth}
                  </p>
                </Card>
                <Card padding="md" className="bg-indigo-50/50 border-indigo-100" data-id="element-17943">
                  <h4 className="font-bold text-indigo-800 mb-2 text-sm" data-id="element-17944">
                    Психосоматика (Луна)
                  </h4>
                  <p className="text-xs text-slate-600" data-id="element-17945">
                    {HEALTH_SECTION.moonHealth}
                  </p>
                </Card>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200" data-id="element-17946">
                <h4 className="font-bold text-slate-800 mb-3 text-sm" data-id="element-17947">
                  Рекомендации по здоровью
                </h4>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-17948">
                  {HEALTH_SECTION.recommendations.split('\n').map((rec, i) => <li key={i} className="flex items-start gap-2 text-xs text-slate-600" data-id="element-17949">
                      <CheckIcon className="w-3.5 h-3.5 mt-0.5 text-emerald-500 flex-shrink-0" data-id="element-17950" />
                      {rec.replace('•', '').trim()}
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* 15. ИТОГОВЫЙ АНАЛИЗ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17951">
          <SectionHeader icon={<SparklesIcon className="w-5 h-5" data-id="element-17953" />} title="Итоговый анализ" subtitle="Синтез натальной карты" color="indigo" data-id="element-17952" />

          <div className="space-y-6 mb-8" data-id="element-17954">
            {renderParagraphs(FINAL_ANALYSIS.summary)}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-17955">
            <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-200" data-id="element-17956">
              <h3 className="font-bold text-indigo-800 mb-3" data-id="element-17957">
                Ключевые качества
              </h3>
              <ul className="space-y-2" data-id="element-17958">
                {FINAL_ANALYSIS.keyQualities.map((q, i) => <li key={i} className="flex items-start gap-2 text-sm text-indigo-900" data-id="element-17959">
                    <StarIcon className="w-4 h-4 mt-0.5 text-indigo-500 flex-shrink-0" data-id="element-17960" />
                    {q}
                  </li>)}
              </ul>
            </div>
            <div className="bg-amber-50 rounded-xl p-5 border border-amber-200" data-id="element-17961">
              <h3 className="font-bold text-amber-800 mb-3" data-id="element-17962">
                Главные темы жизни
              </h3>
              <ul className="space-y-2" data-id="element-17963">
                {FINAL_ANALYSIS.lifeThemes.map((t, i) => <li key={i} className="flex items-start gap-2 text-sm text-amber-900" data-id="element-17964">
                    <CompassIcon className="w-4 h-4 mt-0.5 text-amber-500 flex-shrink-0" data-id="element-17965" />
                    {t}
                  </li>)}
              </ul>
            </div>
          </div>

          <h3 className="font-bold text-slate-800 mb-4" data-id="element-17966">
            Ваши персональные аффирмации
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8" data-id="element-17967">
            {PERSONAL_AFFIRMATIONS.map((aff, i) => <div key={i} className={`p-4 rounded-xl border border-slate-200 bg-gradient-to-br ${aff.gradient}`} data-id="element-17968">
                <p className="font-medium text-slate-800 text-sm mb-2" data-id="element-17969">
                  «{aff.text}»
                </p>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider" data-id="element-17970">
                  {aff.source}
                </p>
              </div>)}
          </div>

          <h3 className="font-bold text-slate-800 mb-4" data-id="element-17971">
            Знаменитости с похожими показателями
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-id="element-17972">
            {CELEBRITY_MATCHES.map((celeb, i) => <div key={i} className="flex items-center gap-4 p-3 rounded-xl border border-slate-100 bg-slate-50" data-id="element-17973">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0 ${celeb.color}`} data-id="element-17974">
                  {celeb.initials}
                </div>
                <div data-id="element-17975">
                  <div className="font-bold text-slate-900 text-sm" data-id="element-17976">
                    {celeb.name}
                  </div>
                  <div className="text-xs text-indigo-600 font-medium mb-0.5" data-id="element-17977">
                    {celeb.sharedPlacement}
                  </div>
                  <div className="text-[10px] text-slate-500" data-id="element-17978">{celeb.note}</div>
                </div>
              </div>)}
          </div>

          <div className="bg-gradient-to-br from-amber-100 via-yellow-50 to-amber-100 rounded-2xl p-6 sm:p-8 text-center shadow-sm border border-amber-200/60" data-id="element-17979">
            <SparklesIcon className="w-8 h-8 text-amber-500 mx-auto mb-4" data-id="element-17980" />
            <p className="text-base sm:text-lg font-semibold leading-relaxed text-slate-800 italic max-w-2xl mx-auto" data-id="element-17981">
              {FINAL_ANALYSIS.finalAdvice}
            </p>
          </div>
        </div>
      </>;
  };
  // ═══════════════════════════════════════════
  // RENDER SOLAR CONTENT
  // ═══════════════════════════════════════════
  const renderSolarContent = () => {
    const planetIcons: Record<string, React.ReactNode> = {
      Меркурий: <MessageCircleIcon className="w-5 h-5" data-id="element-17982" />,
      Венера: <HeartIcon className="w-5 h-5" data-id="element-17983" />,
      Марс: <ZapIcon className="w-5 h-5" data-id="element-17984" />,
      Юпитер: <TrendingUpIcon className="w-5 h-5" data-id="element-17985" />,
      Сатурн: <ShieldIcon className="w-5 h-5" data-id="element-17986" />,
      Уран: <SparklesIcon className="w-5 h-5" data-id="element-17987" />,
      Нептун: <CompassIcon className="w-5 h-5" data-id="element-17988" />,
      Плутон: <ActivityIcon className="w-5 h-5" data-id="element-17989" />
    };
    const areaIcons: Record<string, React.ReactNode> = {
      briefcase: <BriefcaseIcon className="w-5 h-5" data-id="element-17990" />,
      heart: <HeartIcon className="w-5 h-5" data-id="element-17991" />,
      home: <StarIcon className="w-5 h-5" data-id="element-17992" />,
      'trending-up': <TrendingUpIcon className="w-5 h-5" data-id="element-17993" />,
      activity: <ActivityIcon className="w-5 h-5" data-id="element-17994" />,
      users: <UsersIcon className="w-5 h-5" data-id="element-17995" />
    };
    const areaColorMap: Record<string, {
      bg: string;
      border: string;
      text: string;
      iconBg: string;
    }> = {
      emerald: {
        bg: 'bg-emerald-50',
        border: 'border-emerald-200',
        text: 'text-emerald-700',
        iconBg: 'bg-emerald-100 text-emerald-600'
      },
      rose: {
        bg: 'bg-rose-50',
        border: 'border-rose-200',
        text: 'text-rose-700',
        iconBg: 'bg-rose-100 text-rose-600'
      },
      amber: {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        text: 'text-amber-700',
        iconBg: 'bg-amber-100 text-amber-600'
      },
      indigo: {
        bg: 'bg-indigo-50',
        border: 'border-indigo-200',
        text: 'text-indigo-700',
        iconBg: 'bg-indigo-100 text-indigo-600'
      },
      teal: {
        bg: 'bg-teal-50',
        border: 'border-teal-200',
        text: 'text-teal-700',
        iconBg: 'bg-teal-100 text-teal-600'
      },
      sky: {
        bg: 'bg-sky-50',
        border: 'border-sky-200',
        text: 'text-sky-700',
        iconBg: 'bg-sky-100 text-sky-600'
      }
    };
    // Helper: render a section header
    const SectionHeader = ({
      icon,
      title,
      subtitle
    }: {
      icon: React.ReactNode;
      title: string;
      subtitle?: string;
    }) => <div className="flex items-center gap-3 mb-4 sm:mb-6" data-id="element-17996">
        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center" data-id="element-17997">
          {icon}
        </div>
        <div className="min-w-0" data-id="element-17998">
          <h2 className="text-lg sm:text-xl font-bold text-slate-900 leading-tight" data-id="element-17999">
            {title}
          </h2>
          {subtitle && <p className="text-xs sm:text-sm text-slate-500" data-id="element-18000">{subtitle}</p>}
        </div>
      </div>;
    // Helper: render strengths/cautions cards
    const StrengthsCautions = ({
      strengths,
      cautions
    }: {
      strengths: string[];
      cautions: string[];
    }) => <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6" data-id="element-18001">
        <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100" data-id="element-18002">
          <h4 className="font-bold text-emerald-800 mb-3 flex items-center gap-2" data-id="element-18003">
            <ShieldIcon className="w-4 h-4" data-id="element-18004" /> Сильные стороны
          </h4>
          <ul className="space-y-1.5 text-sm text-emerald-700" data-id="element-18005">
            {strengths.map((s, i) => <li key={i} className="flex items-start gap-2" data-id="element-18006">
                <CheckIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-18007" />
                {s}
              </li>)}
          </ul>
        </div>
        <div className="bg-amber-50 rounded-xl p-4 border border-amber-100" data-id="element-18008">
          <h4 className="font-bold text-amber-800 mb-3 flex items-center gap-2" data-id="element-18009">
            <AlertTriangleIcon className="w-4 h-4" data-id="element-18010" /> Зоны внимания
          </h4>
          <ul className="space-y-1.5 text-sm text-amber-700" data-id="element-18011">
            {cautions.map((c, i) => <li key={i} className="flex items-start gap-2" data-id="element-18012">
                <AlertTriangleIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-18013" />
                {c}
              </li>)}
          </ul>
        </div>
      </div>;
    // Individual planet sections (Mercury, Venus, Mars shown separately)
    const individualPlanets = SOLAR_PLANETS_IN_HOUSES.slice(0, 3);
    // Grouped planets (Jupiter, Saturn, outer)
    const groupedPlanets = SOLAR_PLANETS_IN_HOUSES.slice(3);
    return <>
        {/* ══════════ 01. ВВЕДЕНИЕ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-18014">
          <SectionHeader icon={<BookOpenIcon className="w-5 h-5" data-id="element-18016" />} title={SOLAR_INTRO.title} subtitle="Что такое соляр и как работает прогноз" data-id="element-18015" />
          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-18017">
            <p data-id="element-18018">{SOLAR_INTRO.whatIsSolar}</p>
            <p data-id="element-18019">{SOLAR_INTRO.howItAffects}</p>
          </div>
        </div>

        {/* ══════════ 02. ОБЩАЯ ТЕМА ГОДА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-amber-50/50 to-white" data-id="element-18020">
          <SectionHeader icon={<SunIcon className="w-5 h-5" data-id="element-18022" />} title="Общая тема года" subtitle="Ключевые энергии 2026 года" data-id="element-18021" />

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-6" data-id="element-18023">
            <Card padding="md" className="bg-white border-amber-200" data-id="element-18024">
              <div className="flex items-center gap-2 mb-3" data-id="element-18025">
                <TargetIcon className="w-5 h-5 text-amber-500" data-id="element-18026" />
                <span className="text-xs font-bold text-amber-600 uppercase tracking-wider" data-id="element-18027">
                  Тема года
                </span>
              </div>
              <div className="text-lg font-bold text-slate-900" data-id="element-18028">
                {SOLAR_YEAR_THEME.mainEnergy}
              </div>
              <p className="text-xs text-slate-500 mt-2" data-id="element-18029">
                Солнце и Сатурн в 10 доме
              </p>
            </Card>
            <Card padding="md" className="bg-white border-orange-200" data-id="element-18030">
              <div className="flex items-center gap-2 mb-3" data-id="element-18031">
                <BriefcaseIcon className="w-5 h-5 text-orange-500" data-id="element-18032" />
                <span className="text-xs font-bold text-orange-600 uppercase tracking-wider" data-id="element-18033">
                  Ключевая сфера
                </span>
              </div>
              <div className="text-lg font-bold text-slate-900" data-id="element-18034">
                Карьера и статус
              </div>
              <p className="text-xs text-slate-500 mt-2" data-id="element-18035">10 дом активирован</p>
            </Card>
            <Card padding="md" className="bg-white border-emerald-200" data-id="element-18036">
              <div className="flex items-center gap-2 mb-3" data-id="element-18037">
                <TrendingUpIcon className="w-5 h-5 text-emerald-500" data-id="element-18038" />
                <span className="text-xs font-bold text-emerald-600 uppercase tracking-wider" data-id="element-18039">
                  Лучший период
                </span>
              </div>
              <div className="text-lg font-bold text-slate-900" data-id="element-18040">Март — Май</div>
              <p className="text-xs text-slate-500 mt-2" data-id="element-18041">
                Максимум возможностей
              </p>
            </Card>
          </div>

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-18042">
            <p data-id="element-18043">{SOLAR_YEAR_THEME.description}</p>
            <p data-id="element-18044">
              <strong className="text-slate-800" data-id="element-18045">Основные изменения:</strong>{' '}
              {SOLAR_YEAR_THEME.changes}
            </p>
            <p data-id="element-18046">
              <strong className="text-slate-800" data-id="element-18047">
                Психологическая атмосфера:
              </strong>{' '}
              {SOLAR_YEAR_THEME.atmosphere}
            </p>
            <p data-id="element-18048">
              <strong className="text-slate-800" data-id="element-18049">Точки роста:</strong>{' '}
              {SOLAR_YEAR_THEME.growthPoints}
            </p>
          </div>

          <div className="mt-6 bg-amber-100/50 rounded-xl p-4 border border-amber-200" data-id="element-18050">
            <h4 className="font-bold text-amber-800 mb-3" data-id="element-18051">
              Главные задачи года
            </h4>
            <ul className="space-y-1.5 text-sm text-amber-700" data-id="element-18052">
              {SOLAR_YEAR_THEME.tasks.map((task, i) => <li key={i} className="flex items-start gap-2" data-id="element-18053">
                  <TargetIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-18054" />
                  {task}
                </li>)}
            </ul>
          </div>
        </div>

        {/* ══════════ 03. АСЦЕНДЕНТ СОЛЯРА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-18055">
          <SectionHeader icon={<StarIcon className="w-5 h-5" data-id="element-18057" />} title={SOLAR_ASCENDANT.title} subtitle={`Восходящий знак: ${SOLAR_ASCENDANT.symbol} ${SOLAR_ASCENDANT.sign}`} data-id="element-18056" />

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-18058">
            <p data-id="element-18059">{SOLAR_ASCENDANT.description}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 my-6" data-id="element-18060">
              <Card padding="md" className="bg-white border-indigo-200" data-id="element-18061">
                <h4 className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-2" data-id="element-18062">
                  Проявление
                </h4>
                <p className="text-sm text-slate-600" data-id="element-18063">
                  {SOLAR_ASCENDANT.manifestation}
                </p>
              </Card>
              <Card padding="md" className="bg-white border-amber-200" data-id="element-18064">
                <h4 className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-2" data-id="element-18065">
                  Стратегия
                </h4>
                <p className="text-sm text-slate-600" data-id="element-18066">
                  {SOLAR_ASCENDANT.strategy}
                </p>
              </Card>
              <Card padding="md" className="bg-white border-emerald-200" data-id="element-18067">
                <h4 className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-2" data-id="element-18068">
                  Восприятие
                </h4>
                <p className="text-sm text-slate-600" data-id="element-18069">
                  {SOLAR_ASCENDANT.perception}
                </p>
              </Card>
            </div>
          </div>
        </div>

        {/* ══════════ 04. СОЛНЦЕ В СОЛЯРЕ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-amber-50/30 to-white" data-id="element-18070">
          <SectionHeader icon={<SunIcon className="w-5 h-5" data-id="element-18072" />} title={SOLAR_SUN_POSITION.title} subtitle={`${SOLAR_SUN_POSITION.sign} • ${SOLAR_SUN_POSITION.house}-й дом`} data-id="element-18071" />

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-18073">
            <p data-id="element-18074">{SOLAR_SUN_POSITION.description}</p>
            <p data-id="element-18075">
              <strong className="text-slate-800" data-id="element-18076">Ключевая сфера:</strong>{' '}
              {SOLAR_SUN_POSITION.keySphere}
            </p>
            <p data-id="element-18077">{SOLAR_SUN_POSITION.focus}</p>
          </div>

          <div className="mt-6 bg-white rounded-xl p-4 border border-amber-200" data-id="element-18078">
            <h4 className="font-bold text-amber-800 mb-3" data-id="element-18079">Возможные события</h4>
            <ul className="space-y-1.5 text-sm text-slate-700" data-id="element-18080">
              {SOLAR_SUN_POSITION.events.map((event, i) => <li key={i} className="flex items-start gap-2" data-id="element-18081">
                  <SunIcon className="w-3.5 h-3.5 mt-0.5 text-amber-500 flex-shrink-0" data-id="element-18082" />
                  {event}
                </li>)}
            </ul>
          </div>

          <StrengthsCautions strengths={SOLAR_SUN_POSITION.strengths} cautions={SOLAR_SUN_POSITION.cautions} data-id="element-18083" />
        </div>

        {/* ══════════ 05. ЛУНА В СОЛЯРЕ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-18084">
          <SectionHeader icon={<MoonIcon className="w-5 h-5" data-id="element-18086" />} title={SOLAR_MOON.title} subtitle={`${SOLAR_MOON.sign} • ${SOLAR_MOON.house}-й дом`} data-id="element-18085" />

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-18087">
            <p data-id="element-18088">{SOLAR_MOON.description}</p>
            <p data-id="element-18089">
              <strong className="text-slate-800" data-id="element-18090">Эмоциональный фон:</strong>{' '}
              {SOLAR_MOON.emotions}
            </p>
            <p data-id="element-18091">
              <strong className="text-slate-800" data-id="element-18092">
                Внутренние потребности:
              </strong>{' '}
              {SOLAR_MOON.needs}
            </p>
            <p data-id="element-18093">
              <strong className="text-slate-800" data-id="element-18094">Отношения с близкими:</strong>{' '}
              {SOLAR_MOON.relations}
            </p>
          </div>

          <StrengthsCautions strengths={SOLAR_MOON.strengths} cautions={SOLAR_MOON.cautions} data-id="element-18095" />
        </div>

        {/* ══════════ 06-08. INDIVIDUAL PLANETS (Mercury, Venus, Mars) ══════════ */}
        {individualPlanets.map((planet, idx) => <div key={idx} className={`p-4 sm:p-6 md:p-8 border-b border-slate-100 ${idx % 2 === 0 ? 'bg-gradient-to-br from-slate-50/50 to-white' : ''}`} data-id="element-18096">
            <SectionHeader icon={planetIcons[planet.planet] || <StarIcon className="w-5 h-5" data-id="element-18098" />} title={planet.title} subtitle={`${planet.symbol} ${planet.sign} • ${planet.house}-й дом`} data-id="element-18097" />
            <p className="text-slate-600 leading-relaxed mb-4" data-id="element-18099">
              {planet.description}
            </p>
            <div className="bg-white rounded-xl p-4 border border-slate-200" data-id="element-18100">
              <h4 className="font-bold text-slate-800 mb-3" data-id="element-18101">
                Возможные события
              </h4>
              <ul className="space-y-1.5 text-sm text-slate-700" data-id="element-18102">
                {planet.events.map((event, i) => <li key={i} className="flex items-start gap-2" data-id="element-18103">
                    <CheckIcon className="w-3.5 h-3.5 mt-0.5 text-amber-500 flex-shrink-0" data-id="element-18104" />
                    {event}
                  </li>)}
              </ul>
            </div>
          </div>)}

        {/* ══════════ 09. ЮПИТЕР, САТУРН И ВНЕШНИЕ ПЛАНЕТЫ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-indigo-50/30 to-white" data-id="element-18105">
          <SectionHeader icon={<LayersIcon className="w-5 h-5" data-id="element-18107" />} title="Юпитер, Сатурн и внешние планеты" subtitle="Долгосрочные тенденции года" data-id="element-18106" />

          <div className="space-y-6" data-id="element-18108">
            {groupedPlanets.map((planet, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: idx * 0.1
          }} className="bg-white rounded-xl p-5 border border-slate-200" data-id="element-18109">
                <div className="flex items-center gap-3 mb-3" data-id="element-18110">
                  <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center text-lg" data-id="element-18111">
                    {planet.symbol}
                  </div>
                  <div data-id="element-18112">
                    <h4 className="font-bold text-slate-900" data-id="element-18113">{planet.title}</h4>
                    <p className="text-xs text-slate-500" data-id="element-18114">
                      {planet.sign} • {planet.house}-й дом
                    </p>
                  </div>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-3" data-id="element-18115">
                  {planet.description}
                </p>
                <div className="flex flex-wrap gap-2" data-id="element-18116">
                  {planet.events.map((event, i) => <span key={i} className="text-xs bg-indigo-50 text-indigo-700 px-2.5 py-1 rounded-full border border-indigo-100" data-id="element-18117">
                      {event}
                    </span>)}
                </div>
              </motion.div>)}
          </div>
        </div>

        {/* ══════════ 10. АСПЕКТЫ СОЛЯРА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-18118">
          <SectionHeader icon={<SparklesIcon className="w-5 h-5" data-id="element-18120" />} title="Аспекты соляра" subtitle="Взаимодействие планетарных энергий года" color="amber" data-id="element-18119" />
          <div className="space-y-4" data-id="element-18121">
            {COMBO_SOLAR_ASPECTS.map((aspect, i) => {
            const isHarmonious = aspect.nature === 'harmonious';
            return <motion.div key={i} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: i * 0.1
            }} className={`rounded-xl p-5 border ${isHarmonious ? 'bg-emerald-50/50 border-emerald-200' : 'bg-rose-50/50 border-rose-200'}`} data-id="element-18122">
                  <div className="flex flex-wrap items-start sm:items-center justify-between gap-2 sm:gap-3 mb-3" data-id="element-18123">
                    <div className="flex items-center gap-2 sm:gap-3" data-id="element-18124">
                      <span className={`text-lg font-bold ${isHarmonious ? 'text-emerald-600' : 'text-rose-600'}`} data-id="element-18125">
                        {aspect.aspect}
                      </span>
                      <h4 className="font-bold text-slate-900 text-sm sm:text-base" data-id="element-18126">
                        {aspect.title}
                      </h4>
                    </div>
                    <Badge variant={isHarmonious ? 'success' : 'warning'} className="text-xs" data-id="element-18127">
                      {isHarmonious ? 'Гармоничный' : 'Напряжённый'}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed" data-id="element-18128">
                    {aspect.text}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>

        {/* ══════════ 11-13. СФЕРЫ ЖИЗНИ ══════════ */}
        {SOLAR_LIFE_AREAS.map((area, idx) => {
        const colors = areaColorMap[area.color] || areaColorMap.emerald;
        // Group: 11 = career+relationships, 12 = family, 13 = growth+health+social
        // Show section headers for grouping
        const showCareerHeader = idx === 0;
        const showRelationsHeader = idx === 1;
        const showGrowthHeader = idx === 3;
        return <Fragment key={area.id} data-id="element-18129">
              {showCareerHeader && <div className="p-4 sm:p-6 md:p-8 pb-0 border-b-0" data-id="element-18130">
                  <SectionHeader icon={<BriefcaseIcon className="w-5 h-5" data-id="element-18132" />} title="Ключевые сферы жизни года" subtitle="Подробный разбор каждой области" data-id="element-18131" />
                </div>}
              <div className={`p-4 sm:p-6 md:p-8 pb-6 ${idx === SOLAR_LIFE_AREAS.length - 1 ? 'border-b border-slate-100' : ''}`} data-id="element-18133">
                <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: idx * 0.08
            }} className={`${colors.bg} rounded-xl p-5 border ${colors.border}`} data-id="element-18134">
                  <div className="flex items-center gap-3 mb-3" data-id="element-18135">
                    <div className={`w-8 h-8 rounded-lg ${colors.iconBg} flex items-center justify-center`} data-id="element-18136">
                      {areaIcons[area.icon] || <StarIcon className="w-4 h-4" data-id="element-18137" />}
                    </div>
                    <h4 className="font-bold text-slate-900" data-id="element-18138">{area.title}</h4>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed" data-id="element-18139">
                    {area.analysis}
                  </p>
                </motion.div>
              </div>
            </Fragment>;
      })}

        {/* ══════════ 14. ВАЖНЫЕ ПЕРИОДЫ ГОДА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-18140">
          <SectionHeader icon={<CalendarIcon className="w-5 h-5" data-id="element-18142" />} title="Важные периоды года" subtitle="Помесячный прогноз активности" data-id="element-18141" />

          {/* Monthly Forecast Chart */}
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-12 gap-1.5 sm:gap-2 mb-6" data-id="element-18143">
            {SOLAR_MONTHLY_FORECAST.map((month, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.05
          }} className="text-center" data-id="element-18144">
                <div className="text-[10px] text-slate-400 mb-1" data-id="element-18145">
                  {month.month}
                </div>
                <div className="relative h-20 bg-slate-100 rounded-full overflow-hidden" data-id="element-18146">
                  <motion.div className={`absolute bottom-0 w-full rounded-full ${month.intensity >= 80 ? 'bg-amber-500' : month.intensity >= 60 ? 'bg-amber-400' : 'bg-amber-300'}`} initial={{
                height: 0
              }} animate={{
                height: `${month.intensity}%`
              }} transition={{
                duration: 0.8,
                delay: i * 0.05
              }} data-id="element-18147" />
                </div>
                <div className="text-[9px] text-slate-400 mt-1 truncate" data-id="element-18148">
                  {month.theme}
                </div>
              </motion.div>)}
          </div>
          <div className="flex justify-center gap-4 mb-8 text-xs text-slate-500" data-id="element-18149">
            <span className="flex items-center gap-1" data-id="element-18150">
              <span className="w-3 h-3 rounded-full bg-amber-500" data-id="element-18151"></span>{' '}
              Высокая активность
            </span>
            <span className="flex items-center gap-1" data-id="element-18152">
              <span className="w-3 h-3 rounded-full bg-amber-300" data-id="element-18153"></span>{' '}
              Умеренная
            </span>
          </div>

          {/* Key Dates Grid */}
          <h3 className="text-lg font-bold text-slate-900 mb-4" data-id="element-18154">
            Ключевые даты года
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" data-id="element-18155">
            {SOLAR_KEY_DATES.map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: i * 0.1
          }} className={`rounded-xl p-3 border ${item.type === 'positive' ? 'bg-emerald-50 border-emerald-200' : item.type === 'caution' ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`} data-id="element-18156">
                <div className={`text-sm font-bold ${item.type === 'positive' ? 'text-emerald-700' : item.type === 'caution' ? 'text-amber-700' : 'text-slate-700'}`} data-id="element-18157">
                  {item.date}
                </div>
                <div className="text-xs text-slate-500 mt-1" data-id="element-18158">{item.event}</div>
              </motion.div>)}
          </div>

          {/* Sample Month Deep Dive */}
          <div className="mt-8" data-id="element-18159">
            <div className="flex items-center gap-3 mb-4" data-id="element-18160">
              <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center" data-id="element-18161">
                <CalendarIcon className="w-4 h-4" data-id="element-18162" />
              </div>
              <div data-id="element-18163">
                <h3 className="font-bold text-slate-900" data-id="element-18164">Март 2026 — Прорыв</h3>
                <p className="text-xs text-slate-500" data-id="element-18165">
                  Самый активный месяц года
                </p>
              </div>
            </div>
            <p className="text-slate-600 leading-relaxed mb-4" data-id="element-18166">
              Март станет поворотным месяцем. Новолуние в вашем 10 доме 15 марта
              запускает новый карьерный цикл. Это идеальное время для запуска
              проектов, переговоров о повышении или смены работы. Юпитер в
              тригоне к натальному Солнцу усиливает вашу уверенность и
              притягивает удачу.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-18167">
              <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100" data-id="element-18168">
                <h4 className="font-bold text-emerald-800 mb-3" data-id="element-18169">
                  Благоприятно
                </h4>
                <ul className="space-y-1.5 text-sm text-emerald-700" data-id="element-18170">
                  <li className="flex items-start gap-2" data-id="element-18171">
                    <CheckIcon className="w-3.5 h-3.5 mt-0.5" data-id="element-18172" />
                    Карьерные переговоры
                  </li>
                  <li className="flex items-start gap-2" data-id="element-18173">
                    <CheckIcon className="w-3.5 h-3.5 mt-0.5" data-id="element-18174" />
                    Запуск проектов
                  </li>
                  <li className="flex items-start gap-2" data-id="element-18175">
                    <CheckIcon className="w-3.5 h-3.5 mt-0.5" data-id="element-18176" />
                    Публичные выступления
                  </li>
                </ul>
              </div>
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-100" data-id="element-18177">
                <h4 className="font-bold text-amber-800 mb-3" data-id="element-18178">Осторожно</h4>
                <ul className="space-y-1.5 text-sm text-amber-700" data-id="element-18179">
                  <li className="flex items-start gap-2" data-id="element-18180">
                    <AlertTriangleIcon className="w-3.5 h-3.5 mt-0.5" data-id="element-18181" />
                    Импульсивные решения
                  </li>
                  <li className="flex items-start gap-2" data-id="element-18182">
                    <AlertTriangleIcon className="w-3.5 h-3.5 mt-0.5" data-id="element-18183" />
                    Переоценка сил
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* ══════════ 15. РЕКОМЕНДАЦИИ НА ГОД ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-amber-50/30 to-white" data-id="element-18184">
          <SectionHeader icon={<TargetIcon className="w-5 h-5" data-id="element-18186" />} title="Рекомендации на год" subtitle="Практические советы по использованию энергий" data-id="element-18185" />

          <div className="space-y-4" data-id="element-18187">
            {SOLAR_RECOMMENDATIONS.map((rec, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.1
          }} className="bg-white rounded-xl p-5 border border-amber-200" data-id="element-18188">
                <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2" data-id="element-18189">
                  <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-xs font-bold" data-id="element-18190">
                    {i + 1}
                  </span>
                  {rec.title}
                </h4>
                <p className="text-sm text-slate-600 leading-relaxed" data-id="element-18191">
                  {rec.text}
                </p>
              </motion.div>)}
          </div>
        </div>

        {/* ══════════ 16. ИТОГ ПРОГНОЗА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-18192">
          <SectionHeader icon={<CompassIcon className="w-5 h-5" data-id="element-18194" />} title="Итог прогноза" subtitle="Резюме вашего солярного года" data-id="element-18193" />

          <div className="space-y-4 text-slate-600 leading-relaxed mb-6" data-id="element-18195">
            <p data-id="element-18196">{SOLAR_SUMMARY.overview}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-18197">
            <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-100" data-id="element-18198">
              <h4 className="font-bold text-emerald-800 mb-3 flex items-center gap-2" data-id="element-18199">
                <TrendingUpIcon className="w-4 h-4" data-id="element-18200" /> Главные возможности
              </h4>
              <p className="text-sm text-emerald-700 leading-relaxed" data-id="element-18201">
                {SOLAR_SUMMARY.opportunities}
              </p>
            </div>
            <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-100" data-id="element-18202">
              <h4 className="font-bold text-indigo-800 mb-3 flex items-center gap-2" data-id="element-18203">
                <BookOpenIcon className="w-4 h-4" data-id="element-18204" /> Ключевые уроки
              </h4>
              <p className="text-sm text-indigo-700 leading-relaxed" data-id="element-18205">
                {SOLAR_SUMMARY.lessons}
              </p>
            </div>
          </div>
        </div>
      </>;
  };
  // ═══════════════════════════════════════════
  // RENDER COMPATIBILITY CONTENT
  // ═══════════════════════════════════════════
  const renderCompatibilityContent = () => {
    const SectionHeader = ({
      icon,
      title,
      subtitle
    }: {
      icon: React.ReactNode;
      title: string;
      subtitle?: string;
    }) => <div className="flex items-center gap-3 mb-4 sm:mb-6" data-id="element-18206">
        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center" data-id="element-18207">
          {icon}
        </div>
        <div className="min-w-0" data-id="element-18208">
          <h2 className="text-lg sm:text-xl font-bold text-slate-900 leading-tight" data-id="element-18209">
            {title}
          </h2>
          {subtitle && <p className="text-xs sm:text-sm text-slate-500" data-id="element-18210">{subtitle}</p>}
        </div>
      </div>;
    const StrengthsCautions = ({
      strengths,
      cautions,
      strengthsTitle,
      cautionsTitle
    }: {
      strengths: string[];
      cautions: string[];
      strengthsTitle?: string;
      cautionsTitle?: string;
    }) => <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6" data-id="element-18211">
        <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100" data-id="element-18212">
          <h4 className="font-bold text-emerald-800 mb-3 flex items-center gap-2" data-id="element-18213">
            <ShieldIcon className="w-4 h-4" data-id="element-18214" />{' '}
            {strengthsTitle || 'Сильные стороны'}
          </h4>
          <ul className="space-y-1.5 text-sm text-emerald-700" data-id="element-18215">
            {strengths.map((s, i) => <li key={i} className="flex items-start gap-2" data-id="element-18216">
                <CheckIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-18217" />
                {s}
              </li>)}
          </ul>
        </div>
        <div className="bg-amber-50 rounded-xl p-4 border border-amber-100" data-id="element-18218">
          <h4 className="font-bold text-amber-800 mb-3 flex items-center gap-2" data-id="element-18219">
            <AlertTriangleIcon className="w-4 h-4" data-id="element-18220" />{' '}
            {cautionsTitle || 'Зоны внимания'}
          </h4>
          <ul className="space-y-1.5 text-sm text-amber-700" data-id="element-18221">
            {cautions.map((c, i) => <li key={i} className="flex items-start gap-2" data-id="element-18222">
                <AlertTriangleIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-18223" />
                {c}
              </li>)}
          </ul>
        </div>
      </div>;
    const houseIconMap: Record<string, React.ReactNode> = {
      sun: <SunIcon className="w-5 h-5" data-id="element-18224" />,
      moon: <MoonIcon className="w-5 h-5" data-id="element-18225" />,
      heart: <HeartIcon className="w-5 h-5" data-id="element-18226" />,
      zap: <ZapIcon className="w-5 h-5" data-id="element-18227" />,
      sparkles: <SparklesIcon className="w-5 h-5" data-id="element-18228" />,
      compass: <CompassIcon className="w-5 h-5" data-id="element-18229" />,
      home: <StarIcon className="w-5 h-5" data-id="element-18230" />
    };
    const houseColorMap: Record<string, {
      bg: string;
      border: string;
      iconBg: string;
    }> = {
      amber: {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        iconBg: 'bg-amber-100 text-amber-600'
      },
      indigo: {
        bg: 'bg-indigo-50',
        border: 'border-indigo-200',
        iconBg: 'bg-indigo-100 text-indigo-600'
      },
      rose: {
        bg: 'bg-rose-50',
        border: 'border-rose-200',
        iconBg: 'bg-rose-100 text-rose-600'
      },
      orange: {
        bg: 'bg-orange-50',
        border: 'border-orange-200',
        iconBg: 'bg-orange-100 text-orange-600'
      },
      purple: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        iconBg: 'bg-purple-100 text-purple-600'
      },
      emerald: {
        bg: 'bg-emerald-50',
        border: 'border-emerald-200',
        iconBg: 'bg-emerald-100 text-emerald-600'
      },
      pink: {
        bg: 'bg-pink-50',
        border: 'border-pink-200',
        iconBg: 'bg-pink-100 text-pink-600'
      }
    };
    const strengthIconMap: Record<string, React.ReactNode> = {
      heart: <HeartIcon className="w-4 h-4" data-id="element-18231" />,
      sparkles: <SparklesIcon className="w-4 h-4" data-id="element-18232" />,
      zap: <ZapIcon className="w-4 h-4" data-id="element-18233" />,
      home: <StarIcon className="w-4 h-4" data-id="element-18234" />,
      compass: <CompassIcon className="w-4 h-4" data-id="element-18235" />
    };
    const strengthColorMap: Record<string, {
      bg: string;
      border: string;
      iconBg: string;
    }> = {
      rose: {
        bg: 'bg-rose-50',
        border: 'border-rose-200',
        iconBg: 'bg-rose-100 text-rose-600'
      },
      amber: {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        iconBg: 'bg-amber-100 text-amber-600'
      },
      pink: {
        bg: 'bg-pink-50',
        border: 'border-pink-200',
        iconBg: 'bg-pink-100 text-pink-600'
      },
      emerald: {
        bg: 'bg-emerald-50',
        border: 'border-emerald-200',
        iconBg: 'bg-emerald-100 text-emerald-600'
      },
      purple: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        iconBg: 'bg-purple-100 text-purple-600'
      }
    };
    return <>
        {/* ══════════ 01. ВВЕДЕНИЕ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-18236">
          <SectionHeader icon={<BookOpenIcon className="w-5 h-5" data-id="element-18238" />} title={COMPAT_INTRO.title} subtitle="Что такое синастрия и как работает анализ" data-id="element-18237" />
          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-18239">
            <p data-id="element-18240">{COMPAT_INTRO.whatIsSynastry}</p>
            <p data-id="element-18241">{COMPAT_INTRO.howItWorks}</p>
            <p data-id="element-18242">{COMPAT_INTRO.whatIsAnalyzed}</p>
          </div>
        </div>

        {/* ══════════ 02. ОБЩАЯ ДИНАМИКА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-rose-50/50 to-white" data-id="element-18243">
          <SectionHeader icon={<HeartIcon className="w-5 h-5" data-id="element-18245" />} title={COMPAT_OVERALL_DYNAMICS.title} subtitle="Энергетика и потенциал вашего союза" data-id="element-18244" />

          {/* Compatibility Scores */}
          <div className="space-y-4 mb-6" data-id="element-18246">
            {COMPATIBILITY_SCORES.map((item, i) => <div key={i} data-id="element-18247">
                <div className="flex justify-between text-sm mb-1.5" data-id="element-18248">
                  <span className="font-medium text-slate-700" data-id="element-18249">
                    {item.area}
                  </span>
                  <span className={`font-bold ${item.percent >= 80 ? 'text-emerald-600' : item.percent >= 60 ? 'text-amber-600' : 'text-rose-600'}`} data-id="element-18250">
                    {item.percent}%
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden" data-id="element-18251">
                  <motion.div className={`${item.color} h-3 rounded-full`} initial={{
                width: 0
              }} animate={{
                width: `${item.percent}%`
              }} transition={{
                duration: 1,
                delay: i * 0.15,
                ease: 'easeOut'
              }} data-id="element-18252" />
                </div>
              </div>)}
          </div>

          <div className="bg-rose-100/50 rounded-xl p-4 border border-rose-200 mb-6" data-id="element-18253">
            <div className="text-center" data-id="element-18254">
              <div className="text-3xl font-bold text-rose-600 mb-1" data-id="element-18255">83%</div>
              <div className="text-sm text-rose-700" data-id="element-18256">
                Общий индекс совместимости
              </div>
            </div>
          </div>

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-18257">
            <p data-id="element-18258">{COMPAT_OVERALL_DYNAMICS.overallEnergy}</p>
            <p data-id="element-18259">{COMPAT_OVERALL_DYNAMICS.potential}</p>
          </div>

          <StrengthsCautions strengths={COMPAT_OVERALL_DYNAMICS.strengths} cautions={COMPAT_OVERALL_DYNAMICS.challenges} cautionsTitle="Ключевые вызовы" data-id="element-18260" />
        </div>

        {/* ══════════ 03. СОЛНЦЕ И ЛИЧНОСТЬ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-18261">
          <SectionHeader icon={<SunIcon className="w-5 h-5" data-id="element-18263" />} title={COMPAT_SUN_INTERACTION.title} subtitle={COMPAT_SUN_INTERACTION.subtitle} data-id="element-18262" />

          {/* Two People's Big Three */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6" data-id="element-18264">
            <div className="bg-rose-50/50 rounded-xl p-4 border border-rose-100" data-id="element-18265">
              <div className="text-sm font-bold text-rose-600 mb-3" data-id="element-18266">
                Алексей
              </div>
              <div className="space-y-2" data-id="element-18267">
                <div className="flex justify-between text-sm" data-id="element-18268">
                  <span className="text-slate-500" data-id="element-18269">☉ Солнце</span>
                  <span className="font-medium text-slate-700" data-id="element-18270">♌ Лев</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-18271">
                  <span className="text-slate-500" data-id="element-18272">☽ Луна</span>
                  <span className="font-medium text-slate-700" data-id="element-18273">
                    ♏ Скорпион
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-18274">
                  <span className="text-slate-500" data-id="element-18275">ASC</span>
                  <span className="font-medium text-slate-700" data-id="element-18276">♐ Стрелец</span>
                </div>
              </div>
            </div>
            <div className="bg-sky-50/50 rounded-xl p-4 border border-sky-100" data-id="element-18277">
              <div className="text-sm font-bold text-sky-600 mb-3" data-id="element-18278">Мария</div>
              <div className="space-y-2" data-id="element-18279">
                <div className="flex justify-between text-sm" data-id="element-18280">
                  <span className="text-slate-500" data-id="element-18281">☉ Солнце</span>
                  <span className="font-medium text-slate-700" data-id="element-18282">♓ Рыбы</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-18283">
                  <span className="text-slate-500" data-id="element-18284">☽ Луна</span>
                  <span className="font-medium text-slate-700" data-id="element-18285">♋ Рак</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-18286">
                  <span className="text-slate-500" data-id="element-18287">ASC</span>
                  <span className="font-medium text-slate-700" data-id="element-18288">♎ Весы</span>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-18289">
            <p data-id="element-18290">{COMPAT_SUN_INTERACTION.description}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6" data-id="element-18291">
              <Card padding="md" className="bg-white border-amber-200" data-id="element-18292">
                <h4 className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-2" data-id="element-18293">
                  Алексей (♌ Лев)
                </h4>
                <p className="text-sm text-slate-600" data-id="element-18294">
                  {COMPAT_SUN_INTERACTION.partner1Goals}
                </p>
              </Card>
              <Card padding="md" className="bg-white border-indigo-200" data-id="element-18295">
                <h4 className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-2" data-id="element-18296">
                  Мария (♓ Рыбы)
                </h4>
                <p className="text-sm text-slate-600" data-id="element-18297">
                  {COMPAT_SUN_INTERACTION.partner2Goals}
                </p>
              </Card>
            </div>
            <p data-id="element-18298">
              <strong className="text-slate-800" data-id="element-18299">Взаимное понимание:</strong>{' '}
              {COMPAT_SUN_INTERACTION.mutualUnderstanding}
            </p>
          </div>
        </div>

        {/* ══════════ 04. ЭМОЦИОНАЛЬНАЯ СОВМЕСТИМОСТЬ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-indigo-50/30 to-white" data-id="element-18300">
          <SectionHeader icon={<MoonIcon className="w-5 h-5" data-id="element-18302" />} title={COMPAT_EMOTIONAL.title} subtitle={COMPAT_EMOTIONAL.subtitle} data-id="element-18301" />

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-18303">
            <p data-id="element-18304">{COMPAT_EMOTIONAL.description}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6" data-id="element-18305">
              <Card padding="md" className="bg-white border-rose-200" data-id="element-18306">
                <h4 className="text-xs font-bold text-rose-600 uppercase tracking-wider mb-2" data-id="element-18307">
                  Алексей (☽ Скорпион)
                </h4>
                <p className="text-sm text-slate-600" data-id="element-18308">
                  {COMPAT_EMOTIONAL.partner1Needs}
                </p>
              </Card>
              <Card padding="md" className="bg-white border-sky-200" data-id="element-18309">
                <h4 className="text-xs font-bold text-sky-600 uppercase tracking-wider mb-2" data-id="element-18310">
                  Мария (☽ Рак)
                </h4>
                <p className="text-sm text-slate-600" data-id="element-18311">
                  {COMPAT_EMOTIONAL.partner2Needs}
                </p>
              </Card>
            </div>

            <p data-id="element-18312">
              <strong className="text-slate-800" data-id="element-18313">
                Эмоциональная поддержка:
              </strong>{' '}
              {COMPAT_EMOTIONAL.emotionalSupport}
            </p>
          </div>

          <StrengthsCautions strengths={COMPAT_EMOTIONAL.strengths} cautions={COMPAT_EMOTIONAL.cautions} data-id="element-18314" />
        </div>

        {/* ══════════ 05. РОМАНТИЧЕСКАЯ СОВМЕСТИМОСТЬ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-18315">
          <SectionHeader icon={<HeartIcon className="w-5 h-5" data-id="element-18317" />} title={COMPAT_ROMANTIC.title} subtitle={COMPAT_ROMANTIC.subtitle} data-id="element-18316" />

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-18318">
            <p data-id="element-18319">{COMPAT_ROMANTIC.description}</p>
            <p data-id="element-18320">
              <strong className="text-slate-800" data-id="element-18321">Романтическая химия:</strong>{' '}
              {COMPAT_ROMANTIC.chemistry}
            </p>
            <p data-id="element-18322">{COMPAT_ROMANTIC.loveStyles}</p>
          </div>

          <StrengthsCautions strengths={COMPAT_ROMANTIC.strengths} cautions={COMPAT_ROMANTIC.cautions} data-id="element-18323" />
        </div>

        {/* ══════════ 06. ИНТЕЛЛЕКТУАЛЬНАЯ СОВМЕСТИМОСТЬ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-sky-50/30 to-white" data-id="element-18324">
          <SectionHeader icon={<MessageCircleIcon className="w-5 h-5" data-id="element-18326" />} title={COMPAT_INTELLECTUAL.title} subtitle={COMPAT_INTELLECTUAL.subtitle} data-id="element-18325" />

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-18327">
            <p data-id="element-18328">{COMPAT_INTELLECTUAL.description}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6" data-id="element-18329">
              <Card padding="md" className="bg-white border-amber-200" data-id="element-18330">
                <h4 className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-2" data-id="element-18331">
                  Стиль Алексея (☿ Лев)
                </h4>
                <p className="text-sm text-slate-600" data-id="element-18332">
                  {COMPAT_INTELLECTUAL.partner1Style}
                </p>
              </Card>
              <Card padding="md" className="bg-white border-indigo-200" data-id="element-18333">
                <h4 className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-2" data-id="element-18334">
                  Стиль Марии (☿ Рыбы)
                </h4>
                <p className="text-sm text-slate-600" data-id="element-18335">
                  {COMPAT_INTELLECTUAL.partner2Style}
                </p>
              </Card>
            </div>

            <p data-id="element-18336">{COMPAT_INTELLECTUAL.communicationDynamics}</p>
          </div>

          <div className="mt-6 bg-amber-50 rounded-xl p-4 border border-amber-200" data-id="element-18337">
            <h4 className="font-bold text-amber-800 mb-3 flex items-center gap-2" data-id="element-18338">
              <AlertTriangleIcon className="w-4 h-4" data-id="element-18339" /> Возможные сложности
            </h4>
            <ul className="space-y-1.5 text-sm text-amber-700" data-id="element-18340">
              {COMPAT_INTELLECTUAL.difficulties.map((d, i) => <li key={i} className="flex items-start gap-2" data-id="element-18341">
                  <AlertTriangleIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-18342" />
                  {d}
                </li>)}
            </ul>
          </div>
        </div>

        {/* ══════════ 07. КАРМИЧЕСКИЕ СВЯЗИ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-18343">
          <SectionHeader icon={<CompassIcon className="w-5 h-5" data-id="element-18345" />} title={COMPAT_KARMIC.title} subtitle={COMPAT_KARMIC.subtitle} data-id="element-18344" />

          <p className="text-slate-600 leading-relaxed mb-6" data-id="element-18346">
            {COMPAT_KARMIC.description}
          </p>

          <div className="space-y-6" data-id="element-18347">
            {[COMPAT_KARMIC.saturnAnalysis, COMPAT_KARMIC.plutoAnalysis, COMPAT_KARMIC.nodesAnalysis].map((analysis, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: idx * 0.1
          }} className="bg-white rounded-xl p-5 border border-purple-200" data-id="element-18348">
                <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-18349">
                  <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-xs font-bold" data-id="element-18350">
                    {idx + 1}
                  </span>
                  {analysis.title}
                </h4>
                <p className="text-sm text-slate-600 leading-relaxed" data-id="element-18351">
                  {analysis.text}
                </p>
              </motion.div>)}
          </div>
        </div>

        {/* ══════════ 08. ПЛАНЕТЫ В ДОМАХ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-rose-50/30 to-white" data-id="element-18352">
          <SectionHeader icon={<LayersIcon className="w-5 h-5" data-id="element-18354" />} title="Планеты партнёра в домах другого" subtitle="Как вы влияете на жизнь друг друга" data-id="element-18353" />

          <div className="space-y-4" data-id="element-18355">
            {COMPAT_PLANETS_IN_HOUSES.map((item, idx) => {
            const colors = houseColorMap[item.color] || houseColorMap.amber;
            return <motion.div key={idx} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: idx * 0.08
            }} className={`${colors.bg} rounded-xl p-5 border ${colors.border}`} data-id="element-18356">
                  <div className="flex items-center gap-3 mb-3" data-id="element-18357">
                    <div className={`w-8 h-8 rounded-lg ${colors.iconBg} flex items-center justify-center`} data-id="element-18358">
                      {houseIconMap[item.icon] || <StarIcon className="w-4 h-4" data-id="element-18359" />}
                    </div>
                    <h4 className="font-bold text-slate-900" data-id="element-18360">{item.title}</h4>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed" data-id="element-18361">
                    {item.text}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>

        {/* ══════════ 09. АСПЕКТЫ МЕЖДУ КАРТАМИ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-18362">
          <SectionHeader icon={<SparklesIcon className="w-5 h-5" data-id="element-18364" />} title="Основные аспекты между картами" subtitle="Взаимодействие планетарных энергий" data-id="element-18363" />

          <div className="space-y-4" data-id="element-18365">
            {COMPAT_ASPECTS_DETAILED.map((aspect, i) => {
            const isHarmonious = aspect.nature === 'harmonious';
            const isIntense = aspect.nature === 'intense';
            return <motion.div key={i} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: i * 0.08
            }} className={`rounded-xl p-5 border ${isHarmonious ? 'bg-emerald-50/50 border-emerald-200' : isIntense ? 'bg-purple-50/50 border-purple-200' : 'bg-rose-50/50 border-rose-200'}`} data-id="element-18366">
                  <div className="flex flex-wrap items-start sm:items-center justify-between gap-2 sm:gap-3 mb-3" data-id="element-18367">
                    <div className="flex items-center gap-2 sm:gap-3" data-id="element-18368">
                      <span className={`text-lg font-bold ${isHarmonious ? 'text-emerald-600' : isIntense ? 'text-purple-600' : 'text-rose-600'}`} data-id="element-18369">
                        {aspect.aspect}
                      </span>
                      <h4 className="font-bold text-slate-900 text-sm sm:text-base" data-id="element-18370">
                        {aspect.title}
                      </h4>
                    </div>
                    <Badge variant={isHarmonious ? 'success' : isIntense ? 'pro' : 'warning'} className="text-xs" data-id="element-18371">
                      {isHarmonious ? 'Гармоничный' : isIntense ? 'Интенсивный' : 'Напряжённый'}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed" data-id="element-18372">
                    {aspect.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>

        {/* ══════════ 10. СИЛЬНЫЕ СТОРОНЫ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-emerald-50/30 to-white" data-id="element-18373">
          <SectionHeader icon={<ShieldIcon className="w-5 h-5" data-id="element-18375" />} title={COMPAT_STRENGTHS.title} subtitle={COMPAT_STRENGTHS.description} data-id="element-18374" />

          <div className="space-y-4" data-id="element-18376">
            {COMPAT_STRENGTHS.items.map((item, idx) => {
            const colors = strengthColorMap[item.color] || strengthColorMap.rose;
            return <motion.div key={idx} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: idx * 0.08
            }} className={`${colors.bg} rounded-xl p-5 border ${colors.border}`} data-id="element-18377">
                  <div className="flex items-center gap-3 mb-3" data-id="element-18378">
                    <div className={`w-8 h-8 rounded-lg ${colors.iconBg} flex items-center justify-center`} data-id="element-18379">
                      {strengthIconMap[item.icon] || <StarIcon className="w-4 h-4" data-id="element-18380" />}
                    </div>
                    <h4 className="font-bold text-slate-900" data-id="element-18381">{item.title}</h4>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed" data-id="element-18382">
                    {item.text}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>

        {/* ══════════ 11. ВОЗМОЖНЫЕ КОНФЛИКТЫ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-18383">
          <SectionHeader icon={<AlertTriangleIcon className="w-5 h-5" data-id="element-18385" />} title={COMPAT_CONFLICTS.title} subtitle={COMPAT_CONFLICTS.description} data-id="element-18384" />

          <div className="space-y-4" data-id="element-18386">
            {COMPAT_CONFLICTS.items.map((item, idx) => {
            const severityColors = {
              high: {
                bg: 'bg-rose-50',
                border: 'border-rose-200',
                badge: 'warning' as const,
                label: 'Высокий'
              },
              medium: {
                bg: 'bg-amber-50',
                border: 'border-amber-200',
                badge: 'neutral' as const,
                label: 'Средний'
              },
              low: {
                bg: 'bg-slate-50',
                border: 'border-slate-200',
                badge: 'success' as const,
                label: 'Низкий'
              }
            };
            const sev = severityColors[item.severity];
            return <motion.div key={idx} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: idx * 0.1
            }} className={`${sev.bg} rounded-xl p-5 border ${sev.border}`} data-id="element-18387">
                  <div className="flex flex-wrap items-start sm:items-center justify-between gap-2 sm:gap-3 mb-3" data-id="element-18388">
                    <h4 className="font-bold text-slate-900 text-sm sm:text-base" data-id="element-18389">
                      {item.title}
                    </h4>
                    <Badge variant={sev.badge} className="text-xs" data-id="element-18390">
                      Риск: {sev.label}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed mb-3" data-id="element-18391">
                    {item.text}
                  </p>
                  <div className="bg-white/60 rounded-lg p-3 border border-white/80" data-id="element-18392">
                    <p className="text-xs text-slate-700" data-id="element-18393">
                      <strong className="text-emerald-700" data-id="element-18394">Решение:</strong>{' '}
                      {item.solution}
                    </p>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>

        {/* ══════════ 12. ДОЛГОСРОЧНЫЙ ПОТЕНЦИАЛ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-purple-50/30 to-white" data-id="element-18395">
          <SectionHeader icon={<TrendingUpIcon className="w-5 h-5" data-id="element-18397" />} title={COMPAT_LONGTERM.title} subtitle={`Общий потенциал: ${COMPAT_LONGTERM.overallScore}%`} data-id="element-18396" />

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-18398">
            <p data-id="element-18399">{COMPAT_LONGTERM.description}</p>
            <p data-id="element-18400">
              <strong className="text-slate-800" data-id="element-18401">Устойчивость союза:</strong>{' '}
              {COMPAT_LONGTERM.sustainability}
            </p>
            <p data-id="element-18402">
              <strong className="text-slate-800" data-id="element-18403">Совместные цели:</strong>{' '}
              {COMPAT_LONGTERM.sharedGoals}
            </p>
          </div>

          <div className="mt-6 bg-purple-100/50 rounded-xl p-4 border border-purple-200" data-id="element-18404">
            <h4 className="font-bold text-purple-800 mb-3" data-id="element-18405">
              Направления развития отношений
            </h4>
            <ul className="space-y-1.5 text-sm text-purple-700" data-id="element-18406">
              {COMPAT_LONGTERM.developmentAreas.map((area, i) => <li key={i} className="flex items-start gap-2" data-id="element-18407">
                  <TrendingUpIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-18408" />
                  {area}
                </li>)}
            </ul>
          </div>
        </div>

        {/* ══════════ 13. РЕКОМЕНДАЦИИ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-18409">
          <SectionHeader icon={<TargetIcon className="w-5 h-5" data-id="element-18411" />} title="Практические рекомендации" subtitle="Советы для укрепления отношений" data-id="element-18410" />

          <div className="space-y-4" data-id="element-18412">
            {COMPAT_RECOMMENDATIONS.map((rec, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.1
          }} className="bg-white rounded-xl p-5 border border-rose-200" data-id="element-18413">
                <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2" data-id="element-18414">
                  <span className="w-6 h-6 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center text-xs font-bold" data-id="element-18415">
                    {i + 1}
                  </span>
                  {rec.title}
                </h4>
                <p className="text-sm text-slate-600 leading-relaxed" data-id="element-18416">
                  {rec.text}
                </p>
              </motion.div>)}
          </div>
        </div>

        {/* ══════════ 14. ИТОГ АНАЛИЗА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-rose-50/30 to-white" data-id="element-18417">
          <SectionHeader icon={<CompassIcon className="w-5 h-5" data-id="element-18419" />} title="Итог анализа" subtitle="Резюме совместимости" data-id="element-18418" />

          <p className="text-slate-600 leading-relaxed mb-6" data-id="element-18420">
            {COMPAT_SUMMARY.overview}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-id="element-18421">
            <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-100" data-id="element-18422">
              <h4 className="font-bold text-emerald-800 mb-3 flex items-center gap-2" data-id="element-18423">
                <CheckIcon className="w-4 h-4" data-id="element-18424" /> Главные плюсы
              </h4>
              <ul className="space-y-1.5 text-sm text-emerald-700" data-id="element-18425">
                {COMPAT_SUMMARY.mainPros.map((p, i) => <li key={i} className="flex items-start gap-2" data-id="element-18426">
                    <CheckIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-18427" />
                    {p}
                  </li>)}
              </ul>
            </div>
            <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-100" data-id="element-18428">
              <h4 className="font-bold text-indigo-800 mb-3 flex items-center gap-2" data-id="element-18429">
                <BookOpenIcon className="w-4 h-4" data-id="element-18430" /> Ключевые уроки
              </h4>
              <ul className="space-y-1.5 text-sm text-indigo-700" data-id="element-18431">
                {COMPAT_SUMMARY.keyLessons.map((l, i) => <li key={i} className="flex items-start gap-2" data-id="element-18432">
                    <CompassIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-18433" />
                    {l}
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </>;
  };
  // ═══════════════════════════════════════════
  // RENDER COMBO CONTENT
  // ═══════════════════════════════════════════
  const renderComboContent = () => {
    const SectionHeader = ({
      icon,
      title,
      subtitle,
      color = 'purple'
    }: {
      icon: React.ReactNode;
      title: string;
      subtitle?: string;
      color?: string;
    }) => <div className="flex items-center gap-3 mb-4 sm:mb-6" data-id="element-18434">
        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-${color}-100 text-${color}-600 flex items-center justify-center`} data-id="element-18435">
          {icon}
        </div>
        <div className="min-w-0" data-id="element-18436">
          <h2 className="text-lg sm:text-xl font-bold text-slate-900 leading-tight" data-id="element-18437">
            {title}
          </h2>
          {subtitle && <p className="text-xs sm:text-sm text-slate-500" data-id="element-18438">{subtitle}</p>}
        </div>
      </div>;
    const PartDivider = ({
      part,
      title,
      subtitle
    }: {
      part: number;
      title: string;
      subtitle: string;
    }) => <div className={`p-4 sm:p-6 md:p-8 border-b border-slate-100 ${part === 1 ? 'bg-gradient-to-r from-indigo-600 to-purple-600' : 'bg-gradient-to-r from-amber-600 to-orange-600'} text-white`} data-id="element-18439">
        <div className="flex items-center gap-3 sm:gap-4" data-id="element-18440">
          <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-white/20 flex items-center justify-center text-xl sm:text-2xl font-bold flex-shrink-0" data-id="element-18441">
            {part}
          </div>
          <div className="min-w-0" data-id="element-18442">
            <h2 className="text-lg sm:text-2xl font-bold" data-id="element-18443">{title}</h2>
            <p className="text-white/70 text-xs sm:text-sm" data-id="element-18444">{subtitle}</p>
          </div>
        </div>
      </div>;
    const lifeAreaIcons: Record<string, React.ReactNode> = {
      briefcase: <BriefcaseIcon className="w-5 h-5" data-id="element-18445" />,
      heart: <HeartIcon className="w-5 h-5" data-id="element-18446" />,
      'trending-up': <TrendingUpIcon className="w-5 h-5" data-id="element-18447" />,
      compass: <CompassIcon className="w-5 h-5" data-id="element-18448" />,
      users: <UsersIcon className="w-5 h-5" data-id="element-18449" />,
      activity: <ActivityIcon className="w-5 h-5" data-id="element-18450" />
    };
    const lifeAreaColors: Record<string, {
      bg: string;
      border: string;
      iconBg: string;
    }> = {
      emerald: {
        bg: 'bg-emerald-50',
        border: 'border-emerald-200',
        iconBg: 'bg-emerald-100 text-emerald-600'
      },
      rose: {
        bg: 'bg-rose-50',
        border: 'border-rose-200',
        iconBg: 'bg-rose-100 text-rose-600'
      },
      indigo: {
        bg: 'bg-indigo-50',
        border: 'border-indigo-200',
        iconBg: 'bg-indigo-100 text-indigo-600'
      },
      teal: {
        bg: 'bg-teal-50',
        border: 'border-teal-200',
        iconBg: 'bg-teal-100 text-teal-600'
      },
      sky: {
        bg: 'bg-sky-50',
        border: 'border-sky-200',
        iconBg: 'bg-sky-100 text-sky-600'
      }
    };
    return <>
        {/* ═══════════════════════════════════ */}
        {/* PART 1 — NATAL CHART */}
        {/* ═══════════════════════════════════ */}
        <PartDivider part={1} title="Натальная карта" subtitle="Анализ личности, потенциала и жизненных особенностей" data-id="element-18451" />

        {/* ══════════ 01. ВВЕДЕНИЕ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-18452">
          <SectionHeader icon={<BookOpenIcon className="w-5 h-5" data-id="element-18454" />} title={COMBO_NATAL_INTRO.title} subtitle="Что такое натальная карта и что она показывает" color="indigo" data-id="element-18453" />
          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-18455">
            <p data-id="element-18456">{COMBO_NATAL_INTRO.whatIsNatalChart}</p>
            <p data-id="element-18457">{COMBO_NATAL_INTRO.whatItShows}</p>
            <p data-id="element-18458">{COMBO_NATAL_INTRO.whatIsAnalyzed}</p>
          </div>
        </div>

        {/* ══════════ 02. ОБЩАЯ ХАРАКТЕРИСТИКА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-indigo-50/50 to-white" data-id="element-18459">
          <SectionHeader icon={<SparklesIcon className="w-5 h-5" data-id="element-18461" />} title={COMBO_PERSONALITY.title} subtitle="Базовые черты, мотивации и ценности" color="indigo" data-id="element-18460" />

          <p className="text-slate-600 leading-relaxed mb-6" data-id="element-18462">
            {COMBO_PERSONALITY.overview}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-id="element-18463">
            {COMBO_PERSONALITY.traits.map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.1
          }} className="bg-white rounded-xl p-4 border border-indigo-200" data-id="element-18464">
                <h4 className="font-bold text-indigo-800 mb-2" data-id="element-18465">{item.trait}</h4>
                <p className="text-sm text-slate-600" data-id="element-18466">{item.description}</p>
              </motion.div>)}
          </div>

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-18467">
            <p data-id="element-18468">
              <strong className="text-slate-800" data-id="element-18469">Мотивации:</strong>{' '}
              {COMBO_PERSONALITY.motivations}
            </p>
            <p data-id="element-18470">
              <strong className="text-slate-800" data-id="element-18471">Ценности:</strong>{' '}
              {COMBO_PERSONALITY.values}
            </p>
          </div>
        </div>

        {/* ══════════ 03. СОЛНЦЕ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-18472">
          <SectionHeader icon={<SunIcon className="w-5 h-5" data-id="element-18474" />} title={COMBO_SUN.title} subtitle={COMBO_SUN.subtitle} color="amber" data-id="element-18473" />
          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-18475">
            <p data-id="element-18476">{COMBO_SUN.description}</p>
            <p data-id="element-18477">
              <strong className="text-slate-800" data-id="element-18478">Жизненные цели:</strong>{' '}
              {COMBO_SUN.lifeGoals}
            </p>
            <p data-id="element-18479">
              <strong className="text-slate-800" data-id="element-18480">Проявление личности:</strong>{' '}
              {COMBO_SUN.personality}
            </p>
            <p data-id="element-18481">
              <strong className="text-slate-800" data-id="element-18482">Внутренний потенциал:</strong>{' '}
              {COMBO_SUN.potential}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6" data-id="element-18483">
            <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100" data-id="element-18484">
              <h4 className="font-bold text-emerald-800 mb-3 flex items-center gap-2" data-id="element-18485">
                <ShieldIcon className="w-4 h-4" data-id="element-18486" /> Суперсилы
              </h4>
              <ul className="space-y-1.5 text-sm text-emerald-700" data-id="element-18487">
                {COMBO_SUN.strengths.map((s, i) => <li key={i} className="flex items-start gap-2" data-id="element-18488">
                    <CheckIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-18489" />
                    {s}
                  </li>)}
              </ul>
            </div>
            <div className="bg-rose-50 rounded-xl p-4 border border-rose-100" data-id="element-18490">
              <h4 className="font-bold text-rose-800 mb-3 flex items-center gap-2" data-id="element-18491">
                <ZapIcon className="w-4 h-4" data-id="element-18492" /> Теневая сторона
              </h4>
              <ul className="space-y-1.5 text-sm text-rose-700" data-id="element-18493">
                {COMBO_SUN.shadows.map((s, i) => <li key={i} className="flex items-start gap-2" data-id="element-18494">
                    <AlertTriangleIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-18495" />
                    {s}
                  </li>)}
              </ul>
            </div>
          </div>
        </div>

        {/* ══════════ 04. ЛУНА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-indigo-50/30 to-white" data-id="element-18496">
          <SectionHeader icon={<MoonIcon className="w-5 h-5" data-id="element-18498" />} title={COMBO_MOON.title} subtitle={COMBO_MOON.subtitle} color="indigo" data-id="element-18497" />
          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-18499">
            <p data-id="element-18500">{COMBO_MOON.description}</p>
            <p data-id="element-18501">
              <strong className="text-slate-800" data-id="element-18502">Эмоциональные реакции:</strong>{' '}
              {COMBO_MOON.emotionalReactions}
            </p>
            <p data-id="element-18503">
              <strong className="text-slate-800" data-id="element-18504">
                Внутренние потребности:
              </strong>{' '}
              {COMBO_MOON.innerNeeds}
            </p>
            <p data-id="element-18505">
              <strong className="text-slate-800" data-id="element-18506">
                Способ переживания чувств:
              </strong>{' '}
              {COMBO_MOON.feelingStyle}
            </p>
          </div>
        </div>

        {/* ══════════ 05. АСЦЕНДЕНТ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-18507">
          <SectionHeader icon={<StarIcon className="w-5 h-5" data-id="element-18509" />} title={COMBO_ASCENDANT.title} subtitle={COMBO_ASCENDANT.subtitle} color="purple" data-id="element-18508" />
          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-18510">
            <p data-id="element-18511">{COMBO_ASCENDANT.description}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 my-6" data-id="element-18512">
              <Card padding="md" className="bg-white border-purple-200" data-id="element-18513">
                <h4 className="text-xs font-bold text-purple-600 uppercase tracking-wider mb-2" data-id="element-18514">
                  Внешнее проявление
                </h4>
                <p className="text-sm text-slate-600" data-id="element-18515">
                  {COMBO_ASCENDANT.externalPresentation}
                </p>
              </Card>
              <Card padding="md" className="bg-white border-amber-200" data-id="element-18516">
                <h4 className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-2" data-id="element-18517">
                  Первое впечатление
                </h4>
                <p className="text-sm text-slate-600" data-id="element-18518">
                  {COMBO_ASCENDANT.firstImpression}
                </p>
              </Card>
              <Card padding="md" className="bg-white border-emerald-200" data-id="element-18519">
                <h4 className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-2" data-id="element-18520">
                  Стиль поведения
                </h4>
                <p className="text-sm text-slate-600" data-id="element-18521">
                  {COMBO_ASCENDANT.behaviorStyle}
                </p>
              </Card>
            </div>
          </div>
        </div>

        {/* ══════════ 06. ПЛАНЕТЫ В ЗНАКАХ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-purple-50/30 to-white" data-id="element-18522">
          <SectionHeader icon={<SparklesIcon className="w-5 h-5" data-id="element-18524" />} title="Планеты в знаках" subtitle="Влияние каждой планеты на вашу личность" color="purple" data-id="element-18523" />
          <div className="space-y-4" data-id="element-18525">
            {COMBO_PLANETS_IN_SIGNS.map((planet, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: idx * 0.06
          }} className="bg-white rounded-xl p-5 border border-slate-200" data-id="element-18526">
                <div className="flex items-center gap-3 mb-3" data-id="element-18527">
                  <div className="w-8 h-8 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center text-lg" data-id="element-18528">
                    {planet.symbol}
                  </div>
                  <div data-id="element-18529">
                    <h4 className="font-bold text-slate-900" data-id="element-18530">
                      {planet.planet} в {planet.sign}
                    </h4>
                    <p className="text-xs text-slate-500" data-id="element-18531">
                      {planet.house}-й дом
                    </p>
                  </div>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-3" data-id="element-18532">
                  {planet.description}
                </p>
                <div className="flex flex-wrap gap-2" data-id="element-18533">
                  {planet.strengths.map((s, i) => <span key={i} className="text-xs bg-purple-50 text-purple-700 px-2.5 py-1 rounded-full border border-purple-100" data-id="element-18534">
                      {s}
                    </span>)}
                </div>
              </motion.div>)}
          </div>
        </div>

        {/* ══════════ 07. ПЛАНЕТЫ В ДОМАХ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-18535">
          <SectionHeader icon={<LayersIcon className="w-5 h-5" data-id="element-18537" />} title="Планеты в домах" subtitle="Активированные сферы жизни" color="indigo" data-id="element-18536" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-18538">
            {COMBO_HOUSES_SUMMARY.map((house, i) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: i * 0.04
          }} className="bg-slate-50 rounded-xl p-4 border border-slate-200" data-id="element-18539">
                <div className="flex items-center gap-2 mb-2" data-id="element-18540">
                  <span className="w-7 h-7 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold" data-id="element-18541">
                    {house.house}
                  </span>
                  <div data-id="element-18542">
                    <span className="text-sm font-bold text-slate-900" data-id="element-18543">
                      {house.theme}
                    </span>
                    <span className="text-xs text-slate-400 ml-2" data-id="element-18544">
                      {house.sign}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-slate-600" data-id="element-18545">{house.text}</p>
              </motion.div>)}
          </div>
        </div>

        {/* ══════════ 08. АСПЕКТЫ НАТАЛЬНОЙ КАРТЫ (COMBO) ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-white border-t border-slate-200" data-id="element-18546">
          <div className="max-w-5xl mx-auto" data-id="element-18547">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-18548">
              <SparklesIcon className="w-5 h-5 text-indigo-500" data-id="element-18549" />
              Аспекты натальной карты
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-18550">
              {COMBO_NATAL_ASPECTS.map((aspect, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: i * 0.03
            }} className="bg-slate-50 rounded-xl p-4 border border-slate-200" data-id="element-18551">
                  <div className="flex items-center gap-2 mb-2" data-id="element-18552">
                    <span className="text-sm font-bold text-slate-900" data-id="element-18553">
                      {aspect.aspect}
                    </span>
                    <Badge variant="neutral" size="sm" className="text-[10px]" data-id="element-18554">
                      {aspect.type}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-600" data-id="element-18555">{aspect.text}</p>
                </motion.div>)}
            </div>
          </div>
        </div>

        {/* ══════════ 09. ЖИЗНЕННЫЕ СФЕРЫ (COMBO) ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-slate-50 border-t border-slate-200" data-id="element-18556">
          <div className="max-w-5xl mx-auto" data-id="element-18557">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-18558">
              <TargetIcon className="w-5 h-5 text-emerald-500" data-id="element-18559" />
              Жизненные сферы
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-18560">
              {Object.values(COMBO_NATAL_LIFE_AREAS).map((area: {
              title: string;
              icon: string;
              text: string;
            }, i: number) => <motion.div key={i} initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: i * 0.05
            }} className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm" data-id="element-18561">
                    <h4 className="text-sm font-bold text-slate-900 mb-2" data-id="element-18562">
                      {area.title}
                    </h4>
                    <p className="text-xs text-slate-600 leading-relaxed" data-id="element-18563">
                      {area.text}
                    </p>
                  </motion.div>)}
            </div>
          </div>
        </div>

        {/* ══════════ 10. СОЛЯР — ВВЕДЕНИЕ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-white border-t-4 border-amber-400" data-id="element-18564">
          <div className="max-w-5xl mx-auto text-center" data-id="element-18565">
            <Badge variant="warning" size="sm" className="mb-3" data-id="element-18566">
              Часть 2
            </Badge>
            <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-18567">
              Солярный возврат
            </h2>
            <p className="text-sm text-slate-500 max-w-2xl mx-auto" data-id="element-18568">
              {COMBO_SOLAR_INTRO}
            </p>
          </div>
        </div>

        {/* ══════════ 11. ЭНЕРГИЯ ГОДА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-slate-50 border-t border-slate-200" data-id="element-18569">
          <div className="max-w-5xl mx-auto" data-id="element-18570">
            <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-18571">
              <SunIcon className="w-5 h-5 text-amber-500" data-id="element-18572" />
              Энергия года
            </h3>
            <div className="bg-white rounded-xl p-5 border border-slate-200" data-id="element-18573">
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-18574">
                {COMBO_YEAR_ENERGY}
              </p>
            </div>
          </div>
        </div>

        {/* ══════════ 12. СОЛЯР — АСЦЕНДЕНТ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-white border-t border-slate-200" data-id="element-18575">
          <div className="max-w-5xl mx-auto" data-id="element-18576">
            <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-18577">
              <CompassIcon className="w-5 h-5 text-indigo-500" data-id="element-18578" />
              Асцендент соляра
            </h3>
            <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-100" data-id="element-18579">
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-18580">
                {COMBO_SOLAR_ASCENDANT}
              </p>
            </div>
          </div>
        </div>

        {/* ══════════ 13. СОЛЯР — СОЛНЦЕ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-slate-50 border-t border-slate-200" data-id="element-18581">
          <div className="max-w-5xl mx-auto" data-id="element-18582">
            <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-18583">
              <SunIcon className="w-5 h-5 text-amber-500" data-id="element-18584" />
              Солнце в соляре
            </h3>
            <div className="bg-white rounded-xl p-5 border border-slate-200" data-id="element-18585">
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-18586">
                {COMBO_SOLAR_SUN}
              </p>
            </div>
          </div>
        </div>

        {/* ══════════ 14. СОЛЯР — ЛУНА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-white border-t border-slate-200" data-id="element-18587">
          <div className="max-w-5xl mx-auto" data-id="element-18588">
            <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-18589">
              <MoonIcon className="w-5 h-5 text-blue-500" data-id="element-18590" />
              Луна в соляре
            </h3>
            <div className="bg-blue-50 rounded-xl p-5 border border-blue-100" data-id="element-18591">
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-18592">
                {COMBO_SOLAR_MOON}
              </p>
            </div>
          </div>
        </div>

        {/* ══════════ 15. СОЛЯР — ПЛАНЕТЫ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-slate-50 border-t border-slate-200" data-id="element-18593">
          <div className="max-w-5xl mx-auto" data-id="element-18594">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-18595">
              <StarIcon className="w-5 h-5 text-purple-500" data-id="element-18596" />
              Планеты в соляре
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-18597">
              {COMBO_SOLAR_PLANETS.map((planet, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: i * 0.04
            }} className="bg-white rounded-xl p-4 border border-slate-200" data-id="element-18598">
                  <h4 className="text-sm font-bold text-slate-900 mb-1" data-id="element-18599">
                    {planet.planet}
                  </h4>
                  <p className="text-[10px] text-slate-400 mb-2" data-id="element-18600">
                    {planet.position}
                  </p>
                  <p className="text-xs text-slate-600" data-id="element-18601">{planet.text}</p>
                </motion.div>)}
            </div>
          </div>
        </div>

        {/* ══════════ 16. СОЛЯР — АСПЕКТЫ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-white border-t border-slate-200" data-id="element-18602">
          <div className="max-w-5xl mx-auto" data-id="element-18603">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-18604">
              <ActivityIcon className="w-5 h-5 text-rose-500" data-id="element-18605" />
              Аспекты соляра
            </h3>
            <div className="space-y-3" data-id="element-18606">
              {COMBO_SOLAR_ASPECTS.map((aspect, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: i * 0.03
            }} className="bg-slate-50 rounded-xl p-4 border border-slate-200" data-id="element-18607">
                  <div className="flex items-center gap-2 mb-1" data-id="element-18608">
                    <span className="text-sm font-bold text-slate-900" data-id="element-18609">
                      {aspect.aspect}
                    </span>
                    <Badge variant="neutral" size="sm" className="text-[10px]" data-id="element-18610">
                      {aspect.type}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-600" data-id="element-18611">{aspect.text}</p>
                </motion.div>)}
            </div>
          </div>
        </div>

        {/* ══════════ 17. КЛЮЧЕВЫЕ ПЕРИОДЫ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-slate-50 border-t border-slate-200" data-id="element-18612">
          <div className="max-w-5xl mx-auto" data-id="element-18613">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-18614">
              <CalendarIcon className="w-5 h-5 text-emerald-500" data-id="element-18615" />
              Ключевые периоды года
            </h3>
            <div className="space-y-4" data-id="element-18616">
              {Object.entries(COMBO_KEY_PERIODS).map(([category, periods]: [string, {
              period: string;
              description: string;
            }[]]) => <div key={category} data-id="element-18617">
                    <h4 className="text-sm font-bold text-slate-700 mb-2 capitalize" data-id="element-18618">
                      {category === 'favorable' ? '🟢 Благоприятные' : category === 'tense' ? '🔴 Напряжённые' : '⚡ Время действовать'}
                    </h4>
                    <div className="space-y-2" data-id="element-18619">
                      {periods.map((period, i) => <motion.div key={i} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: i * 0.05
                }} className="bg-white rounded-xl p-4 border border-slate-200 flex gap-4" data-id="element-18620">
                          <div className="w-20 shrink-0 text-center" data-id="element-18621">
                            <div className="text-xs font-bold text-indigo-600 bg-indigo-50 rounded-lg px-2 py-1" data-id="element-18622">
                              {period.period}
                            </div>
                          </div>
                          <div data-id="element-18623">
                            <p className="text-xs text-slate-600" data-id="element-18624">
                              {period.description}
                            </p>
                          </div>
                        </motion.div>)}
                    </div>
                  </div>)}
            </div>
          </div>
        </div>

        {/* ══════════ 18. ВЗАИМОДЕЙСТВИЕ СОЛЯР + НАТАЛ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-white border-t border-slate-200" data-id="element-18625">
          <div className="max-w-5xl mx-auto" data-id="element-18626">
            <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-18627">
              <LayersIcon className="w-5 h-5 text-purple-500" data-id="element-18628" />
              Взаимодействие соляра и натала
            </h3>
            <div className="bg-purple-50 rounded-xl p-5 border border-purple-100" data-id="element-18629">
              <h4 className="font-bold text-slate-900 mb-2" data-id="element-18630">
                {COMBO_SOLAR_NATAL_INTERACTION.title}
              </h4>
              <p className="text-sm text-slate-700 leading-relaxed mb-3" data-id="element-18631">
                {COMBO_SOLAR_NATAL_INTERACTION.description}
              </p>
              <div className="space-y-2 mb-3" data-id="element-18632">
                {COMBO_SOLAR_NATAL_INTERACTION.activatedThemes.map((item: {
                theme: string;
                activation: string;
              }, i: number) => <div key={i} className="bg-white/60 rounded-lg p-3 border border-purple-100" data-id="element-18633">
                      <div className="text-sm font-semibold text-purple-800 mb-1" data-id="element-18634">
                        {item.theme}
                      </div>
                      <p className="text-xs text-slate-600" data-id="element-18635">
                        {item.activation}
                      </p>
                    </div>)}
              </div>
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-18636">
                {COMBO_SOLAR_NATAL_INTERACTION.importantEvents}
              </p>
            </div>
          </div>
        </div>

        {/* ══════════ ИТОГОВЫЙ АНАЛИЗ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-gradient-to-b from-indigo-50 to-white border-t border-indigo-100" data-id="element-18637">
          <div className="max-w-5xl mx-auto text-center" data-id="element-18638">
            <SparklesIcon className="w-8 h-8 text-indigo-500 mx-auto mb-3" data-id="element-18639" />
            <h2 className="text-xl font-bold text-slate-900 mb-4" data-id="element-18640">
              Итоговый анализ
            </h2>
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm text-left" data-id="element-18641">
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-18642">
                Ваш комбо-анализ объединяет натальную карту и солярный возврат,
                создавая целостную картину вашей личности и предстоящего года.
                Используйте эти знания как компас для принятия решений и
                раскрытия своего потенциала.
              </p>
            </div>
          </div>
        </div>

        {/* ══════════ FOOTER ══════════ */}
        <div className="p-4 sm:p-6 bg-slate-50 border-t border-slate-200" data-id="element-18643">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 max-w-5xl mx-auto" data-id="element-18644">
            <div className="text-center sm:text-left" data-id="element-18645">
              <p className="text-sm font-medium text-slate-700" data-id="element-18646">
                Astriqo • Premium отчёт
              </p>
              <p className="text-xs text-slate-500 mt-1" data-id="element-18647">
                Отчёт сгенерирован {new Date().toLocaleDateString('ru-RU')} •
                Все права защищены
              </p>
            </div>
            <button onClick={handlePrint} className="report-print-btn flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 hover:bg-slate-100 text-slate-600 hover:text-slate-800 transition-colors text-sm font-medium" data-id="element-18648">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-18649">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" data-id="element-18650" />
                <polyline points="7 10 12 15 17 10" data-id="element-18651" />
                <line x1="12" y1="15" x2="12" y2="3" data-id="element-18652" />
              </svg>
              Скачать PDF
            </button>
          </div>
        </div>

        {/* ══════════ CTA BLOCK ══════════ */}
        <div className="p-6 sm:p-8 md:p-10 bg-white border-t border-slate-200 text-center print:hidden" data-id="element-18653">
          <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-3" data-id="element-18654">
            Хотите персональный разбор от профессионала?
          </h3>
          <p className="text-sm sm:text-base text-slate-500 mb-6 max-w-lg mx-auto" data-id="element-18655">
            Закажите индивидуальную консультацию с опытным астрологом для
            глубокого анализа вашей карты
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3" data-id="element-18656">
            <button className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-sm font-medium transition-colors" data-id="element-18657">
              <UsersIcon className="w-4 h-4" data-id="element-18658" />
              Каталог астрологов
            </button>
            <button className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition-colors shadow-sm" data-id="element-18659">
              Заказать консультацию
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-18660">
                <line x1="5" y1="12" x2="19" y2="12" data-id="element-18661" />
                <polyline points="12 5 19 12 12 19" data-id="element-18662" />
              </svg>
            </button>
          </div>
        </div>

        {/* ══════════ REVIEW SECTION ══════════ */}
        <div className="p-6 sm:p-8 md:p-10 bg-slate-50 border-t border-slate-200 print:hidden" data-id="element-18663">
          <div className="max-w-lg mx-auto text-center" data-id="element-18664">
            {!reviewSubmitted ? <div data-id="element-18665">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4" data-id="element-18666">
                  <SparklesIcon className="w-6 h-6 text-amber-500" data-id="element-18667" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-18668">
                  Как вам отчёт?
                </h3>
                <p className="text-sm text-slate-500 mb-6" data-id="element-18669">
                  Ваш отзыв поможет улучшить качество наших отчётов
                </p>

                {/* Star Rating */}
                <div className="flex items-center justify-center gap-2 mb-6" data-id="element-18670">
                  {[1, 2, 3, 4, 5].map(star => <button key={star} onClick={() => setReviewRating(star)} className="transition-transform hover:scale-110" data-id="element-18671">
                      <StarIcon className={`w-8 h-8 transition-colors ${star <= reviewRating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} data-id="element-18672" />
                    </button>)}
                </div>

                {reviewRating > 0 && <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="space-y-4" data-id="element-18673">
                    {/* Quick Tags */}
                    <div className="flex flex-wrap justify-center gap-2" data-id="element-18674">
                      {['Точность', 'Глубина', 'Оформление', 'Практичность', 'Персонализация', 'Читаемость'].map(tag => <button key={tag} onClick={() => setReviewTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag])} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${reviewTags.includes(tag) ? 'bg-indigo-100 text-indigo-700 border border-indigo-200' : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300'}`} data-id="element-18675">
                          {tag}
                        </button>)}
                    </div>

                    {/* Text Area */}
                    <textarea value={reviewText} onChange={e => setReviewText(e.target.value)} placeholder="Расскажите подробнее (необязательно)" rows={3} className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-400 resize-none" data-id="element-18676" />

                    {/* Submit */}
                    <button onClick={() => setReviewSubmitted(true)} className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-medium transition-colors shadow-sm" data-id="element-18677">
                      Отправить отзыв
                    </button>

                    <p className="text-xs text-slate-400 mt-2" data-id="element-18678">
                      💎 Оставьте отзыв и получите скидку 10% на следующий отчёт
                    </p>
                  </motion.div>}
              </div> : <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} data-id="element-18679">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4" data-id="element-18680">
                  <CheckIcon className="w-6 h-6 text-emerald-600" data-id="element-18681" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-18682">
                  Спасибо за ваш отзыв!
                </h3>
                <p className="text-sm text-slate-500 mb-4" data-id="element-18683">
                  Скидка 10% уже применена к вашему аккаунту
                </p>
                <div className="flex items-center justify-center gap-1" data-id="element-18684">
                  {[1, 2, 3, 4, 5].map(star => <StarIcon key={star} className={`w-5 h-5 ${star <= reviewRating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} data-id="element-18685" />)}
                </div>
              </motion.div>}
          </div>
        </div>
      </>;
  };
  // ═══════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════
  switch (serviceType) {
    case 'natal':
      return renderNatalContent();
    case 'solar':
      return renderSolarContent();
    case 'compatibility':
      return renderCompatibilityContent();
    case 'combo':
      return renderComboContent();
    case 'forecast':
      return renderSolarContent();
    default:
      return renderNatalContent();
  }
}